import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Send, CheckCircle, Clock, XCircle, AlertCircle, Copy, Edit2, Upload, X, AlertTriangle, Sparkles, Loader2, Mail, Users, Search } from 'lucide-react';
import { WhatsAppI18nSection } from '../components/whatsapp/WhatsAppI18nSection';
import { WhatsAppCatalogSection } from '../components/whatsapp/WhatsAppCatalogSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Editable Template Card Component with Upload to Meta functionality
const TemplateCard = ({ tpl, copyToClipboard, onEdit, onUploadToMeta, uploadingTemplate, metaStatus }) => {
  const isUploading = uploadingTemplate === tpl.id;

  // Status-Badge — zeigt auf einen Blick ob das Template bei Meta schon
  // hochgeladen, in Prüfung oder noch nicht eingereicht ist.
  // User-Wunsch 05/2026: „Ich sehe nicht welche Vorlagen schon
  // hochgeladen sind und welche nicht."
  const statusBadge = (() => {
    switch (metaStatus) {
      case 'APPROVED':
        return {
          label: '✓ Hochgeladen',
          className: 'bg-green-100 text-green-800 border border-green-300',
        };
      case 'PENDING':
      case 'IN_REVIEW':
        return {
          label: '⏳ In Prüfung',
          className: 'bg-amber-100 text-amber-800 border border-amber-300',
        };
      case 'REJECTED':
        return {
          label: '✗ Abgelehnt',
          className: 'bg-red-100 text-red-800 border border-red-300',
        };
      case 'PAUSED':
      case 'DISABLED':
        return {
          label: '⏸ Pausiert',
          className: 'bg-gray-100 text-gray-700 border border-gray-300',
        };
      default:
        return {
          label: '↗ Nicht hochgeladen',
          className: 'bg-orange-50 text-orange-700 border border-orange-300',
        };
    }
  })();

  return (
    <div className={`bg-white border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow ${
      metaStatus === 'APPROVED' ? 'border-green-200' :
      metaStatus === 'PENDING' || metaStatus === 'IN_REVIEW' ? 'border-amber-200' :
      metaStatus === 'REJECTED' ? 'border-red-200' :
      !metaStatus ? 'border-orange-200 bg-orange-50/30' :
      'border-gray-200'
    }`} data-testid={`whatsapp-template-card-${tpl.id}`}>
      <div className="flex justify-between items-start mb-2 gap-2">
        <h4 className="font-semibold text-gray-900 text-sm flex-1">{tpl.title}</h4>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded font-semibold whitespace-nowrap ${statusBadge.className}`}
            data-testid={`whatsapp-template-status-${tpl.id}`}
          >
            {statusBadge.label}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded ${tpl.category === 'MARKETING' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
            {tpl.category}
          </span>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-2">{tpl.description}</p>
      
      <div className="space-y-1 text-xs">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-600 w-16">Name:</span>
          <code className="bg-gray-100 px-2 py-0.5 rounded flex-1 text-xs min-w-0 break-all">{tpl.name}</code>
          <button 
            onClick={() => copyToClipboard(tpl.name)}
            className="text-blue-600 hover:text-blue-700 p-1"
            title="Name kopieren"
            data-testid={`whatsapp-template-copy-name-${tpl.id}`}
          >
            <Copy size={12} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-600 w-16">Sprache:</span>
          <span>{tpl.language}</span>
        </div>
      </div>

      <div className="mt-2 p-2 bg-gray-50 rounded border text-xs">
        <div className="flex justify-between items-center mb-1">
          <span className="font-semibold text-gray-700">Body-Text:</span>
          <button 
            onClick={() => copyToClipboard(tpl.body)}
            className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
            data-testid={`whatsapp-template-copy-body-${tpl.id}`}
          >
            <Copy size={12} /> Kopieren
          </button>
        </div>
        <p className="text-gray-800 whitespace-pre-line text-xs break-words">{tpl.body}</p>
      </div>

      <div className="mt-2">
        <span className="text-xs font-semibold text-gray-700">Variablen:</span>
        <div className="flex flex-wrap gap-1 mt-1">
          {tpl.variables.map((v, i) => (
            <span key={i} className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">
              {`{{${i + 1}}}`} = {v}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-2 p-1.5 bg-green-50 border border-green-200 rounded text-xs">
        <span className="font-semibold text-green-700">Beispiel-Werte:</span>
        <span className="text-green-800 ml-1 break-all">{tpl.example.join(', ')}</span>
      </div>
      
      {/* Action Buttons */}
      <div className="mt-3 flex gap-2">
        <button
          onClick={() => onEdit(tpl)}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors"
          data-testid={`whatsapp-template-edit-${tpl.id}`}
        >
          <Edit2 size={12} /> Bearbeiten
        </button>
        <button
          onClick={() => onUploadToMeta(tpl)}
          disabled={isUploading}
          className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded transition-colors ${
            isUploading 
              ? 'bg-gray-100 text-gray-400 cursor-wait' 
              : 'bg-green-50 text-green-700 hover:bg-green-100'
          }`}
          data-testid={`whatsapp-template-upload-${tpl.id}`}
        >
          {isUploading ? (
            <>
              <Clock size={12} className="animate-spin" /> Hochladen...
            </>
          ) : (
            <>
              <Upload size={12} /> Zu Meta hochladen
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Edit Template Dialog Component - Full Featured
const EditTemplateDialog = ({ template, onSave, onClose, onUploadToMeta, onDeleteOldTemplate, onRefresh }) => {
  // Basic fields
  const [editedBody, setEditedBody] = useState(template?.body || '');
  const [editedName, setEditedName] = useState(template?.name || '');
  const [editedLanguage, setEditedLanguage] = useState(template?.language || 'de');
  const [editedCategory, setEditedCategory] = useState(template?.category || 'UTILITY');
  const [editedDescription, setEditedDescription] = useState(template?.description || '');
  const [editedExamples, setEditedExamples] = useState(template?.example || []);
  
  // Header options
  const [hasHeader, setHasHeader] = useState(template?.hasHeader || false);
  const [headerType, setHeaderType] = useState(template?.headerType || 'TEXT');
  const [headerText, setHeaderText] = useState(template?.headerText || '');
  const [headerMediaUrl, setHeaderMediaUrl] = useState(template?.headerMediaUrl || '');
  
  // Footer
  const [hasFooter, setHasFooter] = useState(template?.hasFooter || false);
  const [footerText, setFooterText] = useState(template?.footerText || '');
  
  // Buttons
  const [buttons, setButtons] = useState(template?.buttons || []);
  
  // Validation & Upload state
  const [validationErrors, setValidationErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [deleteOldTemplate, setDeleteOldTemplate] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Active tab for organization
  const [activeTab, setActiveTab] = useState('body');
  
  const requiredVariables = template?.variables || [];
  
  // Button type options
  const buttonTypes = [
    { value: 'URL', label: '🔗 URL Link', description: 'Link zu Website oder App' },
    { value: 'PHONE_NUMBER', label: '📞 Telefonnummer', description: 'Anruf-Button' },
    { value: 'QUICK_REPLY', label: '💬 Schnellantwort', description: 'Vordefinierte Antwort' },
    { value: 'COPY_CODE', label: '📋 Code kopieren', description: 'Kopiert einen Code' }
  ];
  
  // Header type options
  const headerTypes = [
    { value: 'TEXT', label: '📝 Text', description: 'Textüberschrift' },
    { value: 'IMAGE', label: '🖼️ Bild', description: 'Bild-Header (JPG, PNG)' },
    { value: 'VIDEO', label: '🎬 Video', description: 'Video-Header (MP4)' },
    { value: 'DOCUMENT', label: '📄 Dokument', description: 'PDF-Dokument' }
  ];
  
  // Validate variables in body text
  const validateVariables = (text) => {
    const errors = [];
    const presentVariables = [];
    
    const matches = text.match(/\{\{\d+\}\}/g) || [];
    matches.forEach(match => {
      const num = parseInt(match.replace(/[{}]/g, ''));
      if (!presentVariables.includes(num)) {
        presentVariables.push(num);
      }
    });
    
    requiredVariables.forEach((varName, index) => {
      const varNum = index + 1;
      if (!presentVariables.includes(varNum)) {
        errors.push({
          type: 'missing',
          variable: `{{${varNum}}}`,
          name: varName,
          message: `Variable {{${varNum}}} (${varName}) fehlt im Text`
        });
      }
    });
    
    presentVariables.forEach(num => {
      if (num > requiredVariables.length) {
        errors.push({
          type: 'extra',
          variable: `{{${num}}}`,
          message: `Variable {{${num}}} ist nicht definiert`
        });
      }
    });
    
    // META API RULE: Variables cannot be at the start or end of the text
    const trimmedText = text.trim();
    if (trimmedText.match(/^\{\{\d+\}\}/)) {
      errors.push({
        type: 'position',
        message: '⚠️ Meta-Regel: Text darf NICHT mit einer Variable beginnen!'
      });
    }
    if (trimmedText.match(/\{\{\d+\}\}$/)) {
      errors.push({
        type: 'position',
        message: '⚠️ Meta-Regel: Text darf NICHT mit einer Variable enden! Fügen Sie Text nach der letzten Variable hinzu.'
      });
    }
    
    return errors;
  };
  
  useEffect(() => {
    const errors = validateVariables(editedBody);
    setValidationErrors(errors);
  }, [editedBody]);
  
  // Button management
  const addButton = (type) => {
    if (buttons.length >= 3) {
      alert('Maximal 3 Buttons erlaubt!');
      return;
    }
    
    // Check limits per type
    const urlCount = buttons.filter(b => b.type === 'URL').length;
    const phoneCount = buttons.filter(b => b.type === 'PHONE_NUMBER').length;
    const quickReplyCount = buttons.filter(b => b.type === 'QUICK_REPLY').length;
    
    if (type === 'URL' && urlCount >= 2) {
      alert('Maximal 2 URL-Buttons erlaubt!');
      return;
    }
    if (type === 'PHONE_NUMBER' && phoneCount >= 1) {
      alert('Maximal 1 Telefon-Button erlaubt!');
      return;
    }
    if (type === 'QUICK_REPLY' && quickReplyCount >= 3) {
      alert('Maximal 3 Schnellantwort-Buttons erlaubt!');
      return;
    }
    
    const newButton = {
      type: type,
      text: '',
      ...(type === 'URL' && { url: '' }),
      ...(type === 'PHONE_NUMBER' && { phone_number: '' }),
    };
    
    setButtons([...buttons, newButton]);
  };
  
  const updateButton = (index, field, value) => {
    const updated = [...buttons];
    updated[index][field] = value;
    setButtons(updated);
  };
  
  const removeButton = (index) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };
  
  // Update example value
  const updateExample = (index, value) => {
    const newExamples = [...editedExamples];
    newExamples[index] = value;
    setEditedExamples(newExamples);
  };
  
  // Insert variable at cursor
  const insertVariable = (varNum) => {
    setEditedBody(prev => prev + `{{${varNum}}}`);
  };
  
  // Build components for Meta API
  const buildComponents = () => {
    const components = [];
    
    // Header
    if (hasHeader) {
      if (headerType === 'TEXT' && headerText.trim()) {
        components.push({
          type: 'HEADER',
          format: 'TEXT',
          text: headerText
        });
      } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType)) {
        components.push({
          type: 'HEADER',
          format: headerType,
          example: {
            header_handle: [headerMediaUrl || 'https://example.com/placeholder']
          }
        });
      }
    }
    
    // Body (required)
    // META API RULE: Text cannot start or end with a variable (even with punctuation after it)
    let bodyText = editedBody.trim();
    
    // Check if text ends with a variable (possibly followed by punctuation only)
    if (bodyText.match(/\{\{\d+\}\}[.!?,;:]*$/)) {
      // If text ends with a variable (+ optional punctuation), add meaningful text
      bodyText = bodyText.replace(/([.!?,;:]*)$/, '') + '. See you soon!';
    }
    
    // Check if text starts with a variable
    if (bodyText.match(/^\{\{\d+\}\}/)) {
      // If text starts with a variable, add "Hello " before it
      bodyText = 'Hi there! ' + bodyText;
    }
    
    const bodyComponent = {
      type: 'BODY',
      text: bodyText
    };
    if (editedExamples.length > 0 && editedExamples.some(ex => ex)) {
      bodyComponent.example = {
        body_text: [editedExamples]
      };
    }
    components.push(bodyComponent);
    
    // Footer
    if (hasFooter && footerText.trim()) {
      components.push({
        type: 'FOOTER',
        text: footerText
      });
    }
    
    // Buttons
    if (buttons.length > 0) {
      const validButtons = buttons.filter(b => b.text.trim());
      if (validButtons.length > 0) {
        components.push({
          type: 'BUTTONS',
          buttons: validButtons.map(btn => {
            const buttonObj = {
              type: btn.type,
              text: btn.text
            };
            
            if (btn.type === 'URL' && btn.url) {
              buttonObj.url = btn.url;
              // If URL contains variables, add example (required by Meta API)
              if (btn.url.includes('{{')) {
                // Use provided example or generate a default one
                const exampleValue = btn.url_example || 'example_value_123';
                buttonObj.example = [exampleValue];
              }
            }
            
            if (btn.type === 'PHONE_NUMBER' && btn.phone_number) {
              buttonObj.phone_number = btn.phone_number;
            }
            
            if (btn.type === 'COPY_CODE' && btn.code) {
              buttonObj.example = [btn.code];
            }
            
            return buttonObj;
          })
        });
      }
    }
    
    return components;
  };
  
  // Handle save locally
  const handleSave = () => {
    if (validationErrors.length > 0) {
      alert('Bitte beheben Sie zuerst die Validierungsfehler!');
      return;
    }
    
    onSave({
      ...template,
      name: editedName.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
      body: editedBody,
      language: editedLanguage,
      category: editedCategory,
      description: editedDescription,
      example: editedExamples,
      hasHeader,
      headerType,
      headerText,
      headerMediaUrl,
      hasFooter,
      footerText,
      buttons
    });
  };
  
  // Handle upload to Meta
  const handleUploadToMeta = async () => {
    if (validationErrors.length > 0) {
      alert('Bitte beheben Sie zuerst die Validierungsfehler!');
      return;
    }
    
    const missingExamples = editedExamples.filter((ex, i) => !ex || ex.trim() === '');
    if (missingExamples.length > 0 && requiredVariables.length > 0) {
      alert('Bitte füllen Sie alle Beispiel-Werte aus!');
      return;
    }
    
    // Check if delete old template is selected
    if (deleteOldTemplate && !showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }
    
    setIsUploading(true);
    setUploadResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const templateName = editedName.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      const components = buildComponents();
      
      // If deleting old template first
      if (deleteOldTemplate && showDeleteConfirm) {
        try {
          // Use XMLHttpRequest to avoid rrweb-recorder interference
          const deleteResult = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('DELETE', `${BACKEND_URL}/api/whatsapp/templates/${template.name}`);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.onload = () => resolve({ ok: xhr.status >= 200 && xhr.status < 300, text: xhr.responseText, status: xhr.status });
            xhr.onerror = () => reject(new Error('Network error'));
            xhr.send();
          });
          
          if (deleteResult.ok) {
            console.log('Old template deleted:', deleteResult.text);
          } else {
            console.log('Could not delete old template:', deleteResult.text);
          }
          // Wait a moment for Meta to process the deletion
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (delErr) {
          console.log('Delete error:', delErr);
        }
      }
      
      // Use XMLHttpRequest to avoid rrweb-recorder interference with Response.clone()
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/whatsapp/templates/create`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          let data;
          try {
            data = JSON.parse(xhr.responseText);
          } catch (e) {
            data = { detail: xhr.responseText || 'Ungültige Server-Antwort' };
          }
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, data, status: xhr.status });
        };
        xhr.onerror = () => reject(new Error('Netzwerkfehler'));
        xhr.send(JSON.stringify({
          name: templateName,
          category: editedCategory,
          language: editedLanguage,
          components: components,
          allow_category_change: true
        }));
      });
      
      if (result.ok) {
        setUploadResult({
          success: true,
          message: `✅ Template "${templateName}" erfolgreich hochgeladen! Status: ${result.data.status || 'PENDING'}`,
          templateId: result.data.id
        });
        setShowDeleteConfirm(false);
        
        // Automatisch Dialog schliessen und Seite neu laden nach 1.5 Sekunden
        setTimeout(() => {
          onClose();
          onRefresh?.();
        }, 1500);
      } else {
        let errorMsg = result.data.detail || result.data.error?.message || 'Unbekannter Fehler';
        
        if (typeof errorMsg === 'object') {
          errorMsg = JSON.stringify(errorMsg);
        }
        
        if (errorMsg.includes('bereits') || errorMsg.includes('exist') || errorMsg.includes('already exists')) {
          errorMsg = `Ein Template mit dem Namen "${templateName}" existiert bereits. Aktivieren Sie "Altes Template löschen" oder wählen Sie einen anderen Namen.`;
        } else if (errorMsg.includes('2388025') || errorMsg.includes('Kategorie')) {
          errorMsg = `⚠️ Kategorie-Konflikt!\n\nDas Template existiert bei Meta als MARKETING, aber Sie versuchen es als UTILITY hochzuladen.\n\n💡 Lösung: Ändern Sie die Kategorie auf MARKETING in den Einstellungen, oder verwenden Sie einen neuen Namen (z.B. ${templateName}_v2).`;
        } else if (errorMsg.includes('4 weeks') || errorMsg.includes('2388023')) {
          errorMsg = `Meta Sperrfrist aktiv! Wählen Sie eine andere Sprache oder warten Sie 4 Wochen.`;
        }
        
        setUploadResult({
          success: false,
          message: `❌ Fehler: ${errorMsg}`
        });
      }
    } catch (error) {
      setUploadResult({
        success: false,
        message: `❌ Netzwerkfehler: ${error.message}`
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  if (!template) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Edit2 size={20} />
            WhatsApp Template bearbeiten
          </h2>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={24} />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="border-b bg-gray-50 px-6 py-2 flex gap-1">
          {[
            { id: 'body', label: '📝 Nachricht', icon: null },
            { id: 'header', label: '🔝 Header', icon: null },
            { id: 'buttons', label: '🔘 Buttons', icon: null },
            { id: 'settings', label: '⚙️ Einstellungen', icon: null }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === tab.id 
                  ? 'bg-white text-green-700 shadow' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Body Tab */}
          {activeTab === 'body' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template-Titel</label>
                <p className="text-lg font-semibold text-gray-900">{template.title}</p>
                <p className="text-sm text-gray-500">{editedDescription}</p>
              </div>
              
              {/* Required Variables */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Variablen (klicken zum Einfügen)
                </label>
                <div className="flex flex-wrap gap-2">
                  {requiredVariables.map((varName, i) => (
                    <button
                      key={i}
                      onClick={() => insertVariable(i + 1)}
                      className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg text-sm hover:bg-amber-200 transition-colors"
                    >
                      <span className="font-mono font-bold">{`{{${i + 1}}}`}</span>
                      <span className="ml-1 text-amber-600">= {varName}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Body Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nachrichtentext *</label>
                <textarea
                  value={editedBody}
                  onChange={(e) => setEditedBody(e.target.value)}
                  rows={6}
                  className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-green-500 ${
                    validationErrors.length > 0 ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Hallo {{1}}, dein Termin für {{2}} wurde bestätigt..."
                />
                
                {/* Validation */}
                {validationErrors.length > 0 ? (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 text-red-700 font-medium mb-1">
                      <AlertTriangle size={16} />
                      Validierungsfehler
                    </div>
                    <ul className="text-sm text-red-600 space-y-1">
                      {validationErrors.map((err, i) => (
                        <li key={i}>• {err.message}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-sm">
                    <CheckCircle size={16} />
                    Alle Variablen korrekt
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="hasFooter"
                    checked={hasFooter}
                    onChange={(e) => setHasFooter(e.target.checked)}
                    className="rounded text-green-600"
                  />
                  <label htmlFor="hasFooter" className="text-sm font-medium text-gray-700">
                    Footer hinzufügen (optionaler Text am Ende)
                  </label>
                </div>
                {hasFooter && (
                  <input
                    type="text"
                    value={footerText}
                    onChange={(e) => setFooterText(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="z.B. Ihr StrongFit Hub Team"
                    maxLength={60}
                  />
                )}
              </div>
              
              {/* Example Values */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Beispiel-Werte (für Meta-Prüfung erforderlich)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {requiredVariables.map((varName, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded w-12 text-center">{`{{${i + 1}}}`}</span>
                      <input
                        type="text"
                        value={editedExamples[i] || ''}
                        onChange={(e) => updateExample(i, e.target.value)}
                        placeholder={varName}
                        className="flex-1 border rounded-lg px-3 py-1.5 text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Header Tab */}
          {activeTab === 'header' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="hasHeader"
                  checked={hasHeader}
                  onChange={(e) => setHasHeader(e.target.checked)}
                  className="rounded text-green-600"
                />
                <label htmlFor="hasHeader" className="text-sm font-medium text-gray-700">
                  Header aktivieren
                </label>
              </div>
              
              {hasHeader && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Header-Typ</label>
                    <div className="grid grid-cols-2 gap-3">
                      {headerTypes.map(type => (
                        <button
                          key={type.value}
                          onClick={() => setHeaderType(type.value)}
                          className={`p-3 border rounded-lg text-left transition-colors ${
                            headerType === type.value 
                              ? 'border-green-500 bg-green-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium text-gray-900">{type.label}</div>
                          <div className="text-xs text-gray-500">{type.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {headerType === 'TEXT' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Header-Text</label>
                      <input
                        type="text"
                        value={headerText}
                        onChange={(e) => setHeaderText(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        placeholder="z.B. Terminbestätigung"
                        maxLength={60}
                      />
                    </div>
                  )}
                  
                  {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Media-URL (für Beispiel)
                      </label>
                      <input
                        type="url"
                        value={headerMediaUrl}
                        onChange={(e) => setHeaderMediaUrl(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        placeholder={
                          headerType === 'IMAGE' ? 'https://example.com/bild.jpg' :
                          headerType === 'VIDEO' ? 'https://example.com/video.mp4' :
                          'https://example.com/dokument.pdf'
                        }
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {headerType === 'IMAGE' && 'Unterstützt: JPG, PNG (max 5MB)'}
                        {headerType === 'VIDEO' && 'Unterstützt: MP4 (max 16MB)'}
                        {headerType === 'DOCUMENT' && 'Unterstützt: PDF (max 100MB)'}
                      </p>
                    </div>
                  )}
                  
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <strong>💡 Tipp:</strong> Bei Bild/Video/Dokument-Headern wird das Medium beim Versenden dynamisch ersetzt.
                      Die URL hier ist nur ein Beispiel für die Meta-Prüfung.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* Buttons Tab */}
          {activeTab === 'buttons' && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>WhatsApp Button-Limits:</strong><br/>
                  • Maximal 3 Buttons insgesamt<br/>
                  • Maximal 2 URL-Buttons<br/>
                  • Maximal 1 Telefon-Button<br/>
                  • Maximal 3 Schnellantwort-Buttons<br/>
                  • <strong>URL-Variablen:</strong> Sie können {`{{1}}`} in URLs verwenden für dynamische Links
                </p>
              </div>
              
              {/* Add Button Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Button hinzufügen</label>
                <div className="grid grid-cols-2 gap-2">
                  {buttonTypes.map(type => (
                    <button
                      key={type.value}
                      onClick={() => addButton(type.value)}
                      disabled={buttons.length >= 3}
                      className="p-3 border border-dashed border-gray-300 rounded-lg text-left hover:border-green-500 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="font-medium text-gray-900">{type.label}</div>
                      <div className="text-xs text-gray-500">{type.description}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Current Buttons */}
              {buttons.length > 0 && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Aktive Buttons ({buttons.length}/3)</label>
                  {buttons.map((btn, index) => (
                    <div key={index} className="p-4 border rounded-lg bg-white shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">
                          {buttonTypes.find(t => t.value === btn.type)?.label || btn.type}
                        </span>
                        <button
                          onClick={() => removeButton(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Button-Text</label>
                          <input
                            type="text"
                            value={btn.text}
                            onChange={(e) => updateButton(index, 'text', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                            placeholder="z.B. Termin ansehen"
                            maxLength={25}
                          />
                        </div>
                        
                        {btn.type === 'URL' && (
                          <div className="space-y-3">
                            {/* URL Type Selection */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">URL-Typ wählen</label>
                              <div className="grid grid-cols-3 gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    updateButton(index, 'url_type', 'static');
                                    updateButton(index, 'url', btn.url_base || 'https://ihre-app.com/link');
                                  }}
                                  className={`p-2 text-xs border rounded-lg text-left transition-colors ${
                                    (!btn.url_type || btn.url_type === 'static') 
                                      ? 'border-green-500 bg-green-50 text-green-700' 
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  <div className="font-medium">🔒 Statisch</div>
                                  <div className="text-gray-500">Fixer Link</div>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    updateButton(index, 'url_type', 'query');
                                    updateButton(index, 'url', (btn.url_base || 'https://ihre-app.com/page') + '?token={{1}}');
                                  }}
                                  className={`p-2 text-xs border rounded-lg text-left transition-colors ${
                                    btn.url_type === 'query' 
                                      ? 'border-green-500 bg-green-50 text-green-700' 
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  <div className="font-medium">🔗 Query variabel</div>
                                  <div className="text-gray-500">url?param={`{{1}}`}</div>
                                </button>
                              </div>
                              
                              {/* Meta Hinweis */}
                              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-xs text-amber-700">
                                  <strong>⚠️ Meta-Regel:</strong> Variablen dürfen nur in Query-Parametern verwendet werden 
                                  (z.B. ?token={`{{1}}`}). URLs mit Variablen im Pfad (z.B. /booking/{`{{1}}`}) werden abgelehnt.
                                </p>
                              </div>
                            </div>
                            
                            {/* URL Input based on type */}
                            {(!btn.url_type || btn.url_type === 'static') && (
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Fixer Link</label>
                                <input
                                  type="url"
                                  value={btn.url || ''}
                                  onChange={(e) => updateButton(index, 'url', e.target.value)}
                                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                                  placeholder="https://ihre-app.com/booking"
                                />
                              </div>
                            )}
                            
                            {btn.url_type === 'query' && (
                              <div className="space-y-2">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Basis-URL (fix)</label>
                                  <input
                                    type="text"
                                    value={btn.url_base || ''}
                                    onChange={(e) => {
                                      updateButton(index, 'url_base', e.target.value);
                                      updateButton(index, 'url', e.target.value + '?token={{1}}');
                                    }}
                                    className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                                    placeholder="https://calorie-sync.com/confirm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Query-Parameter Name</label>
                                  <input
                                    type="text"
                                    value={btn.query_param || 'token'}
                                    onChange={(e) => {
                                      updateButton(index, 'query_param', e.target.value);
                                      updateButton(index, 'url', (btn.url_base || 'https://app.com') + '?' + e.target.value + '={{1}}');
                                    }}
                                    className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                                    placeholder="token"
                                  />
                                </div>
                                <div className="p-2 bg-gray-100 rounded-lg">
                                  <p className="text-xs text-gray-600">
                                    <strong>Vollständige URL:</strong><br/>
                                    <code className="bg-white px-1 rounded text-green-700">{btn.url_base || 'https://...'}</code>
                                    <code className="bg-gray-200 px-1 rounded">?{btn.query_param || 'token'}=</code>
                                    <code className="bg-blue-100 px-1 rounded text-blue-700">{'{{1}}'}</code>
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {/* Example value input - show for all variable types */}
                            {btn.url && btn.url.includes('{{') && (
                              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                                <label className="block text-xs font-medium text-blue-700">
                                  Beispiel-Wert für Variable {`{{1}}`} (für Meta-Prüfung)
                                </label>
                                <input
                                  type="text"
                                  value={btn.url_example || ''}
                                  onChange={(e) => updateButton(index, 'url_example', e.target.value)}
                                  className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm"
                                  placeholder="z.B. abc123 oder termin_456"
                                />
                                <p className="text-xs text-blue-600">
                                  <strong>Vorschau:</strong> {btn.url.replace(/\{\{1\}\}/g, btn.url_example || '[WERT]')}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {btn.type === 'PHONE_NUMBER' && (
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Telefonnummer (internationales Format)</label>
                            <input
                              type="tel"
                              value={btn.phone_number || ''}
                              onChange={(e) => updateButton(index, 'phone_number', e.target.value)}
                              className="w-full border rounded-lg px-3 py-2 text-sm"
                              placeholder="+41791234567"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Format: +[Ländercode][Nummer] ohne Leerzeichen
                            </p>
                          </div>
                        )}
                        
                        {btn.type === 'QUICK_REPLY' && (
                          <p className="text-xs text-gray-500">
                            Der Button-Text wird als Schnellantwort gesendet wenn der Kunde darauf klickt.
                          </p>
                        )}
                        
                        {btn.type === 'COPY_CODE' && (
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Code zum Kopieren</label>
                            <input
                              type="text"
                              value={btn.code || ''}
                              onChange={(e) => updateButton(index, 'code', e.target.value)}
                              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                              placeholder="z.B. RABATT20"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {buttons.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>Keine Buttons hinzugefügt</p>
                  <p className="text-sm">Klicken Sie oben, um Buttons hinzuzufügen</p>
                </div>
              )}
            </div>
          )}
          
          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template-Name</label>
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="template_name"
                />
                <p className="text-xs text-gray-500 mt-1">Nur Kleinbuchstaben, Zahlen und Unterstriche</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sprache</label>
                  <select
                    value={editedLanguage}
                    onChange={(e) => setEditedLanguage(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="de">Deutsch (de)</option>
                    <option value="en_US">English US</option>
                    <option value="en_GB">English UK</option>
                    <option value="fr">Französisch</option>
                    <option value="it">Italienisch</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie</label>
                  <select
                    value={editedCategory}
                    onChange={(e) => setEditedCategory(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="UTILITY">UTILITY (Transaktional)</option>
                    <option value="MARKETING">MARKETING</option>
                    <option value="AUTHENTICATION">AUTHENTICATION</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
                <input
                  type="text"
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              
              {/* Delete old template option */}
              <div className="border-t pt-4 mt-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="deleteOld"
                      checked={deleteOldTemplate}
                      onChange={(e) => {
                        setDeleteOldTemplate(e.target.checked);
                        setShowDeleteConfirm(false);
                      }}
                      className="mt-1 rounded text-yellow-600"
                    />
                    <div>
                      <label htmlFor="deleteOld" className="text-sm font-medium text-yellow-800 cursor-pointer">
                        Altes Template vor Upload löschen
                      </label>
                      <p className="text-xs text-yellow-700 mt-1">
                        Löscht das bestehende Template "{template.name}" bei Meta bevor das neue hochgeladen wird.
                        Sie werden vor dem Löschen nochmals um Bestätigung gebeten.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Upload Result */}
          {uploadResult && (
            <div className={`mt-4 p-4 rounded-lg ${uploadResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <p className={`text-sm ${uploadResult.success ? 'text-green-700' : 'text-red-700'}`}>
                {uploadResult.message}
              </p>
            </div>
          )}
          
          {/* Delete Confirmation */}
          {showDeleteConfirm && (
            <div className="mt-4 p-4 bg-red-50 border border-red-300 rounded-lg">
              <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                <AlertTriangle size={20} />
                Löschung bestätigen
              </div>
              <p className="text-sm text-red-600 mb-3">
                Das bestehende Template "{template.name}" wird bei Meta gelöscht und durch die neue Version ersetzt.
                Dieser Vorgang kann nicht rückgängig gemacht werden.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleUploadToMeta}
                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Ja, löschen und hochladen
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="border-t bg-gray-50 px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
          >
            Schliessen
          </button>
          <button
            onClick={handleSave}
            disabled={validationErrors.length > 0}
            className={`px-4 py-2 text-sm rounded-lg flex items-center gap-2 ${
              validationErrors.length > 0 
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <CheckCircle size={16} /> Lokal speichern
          </button>
          <button
            onClick={handleUploadToMeta}
            disabled={validationErrors.length > 0 || isUploading}
            className={`flex-1 px-4 py-2 text-sm rounded-lg flex items-center justify-center gap-2 ${
              validationErrors.length > 0 || isUploading
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {isUploading ? (
              <>
                <Clock size={16} className="animate-spin" /> Wird hochgeladen...
              </>
            ) : (
              <>
                <Upload size={16} /> Zu Meta hochladen
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const WhatsAppTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [appVisibleSearch, setAppVisibleSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [wabaConfigured, setWabaConfigured] = useState(true);
  const [teamAlertLoading, setTeamAlertLoading] = useState(false);
  const [teamAlertResult, setTeamAlertResult] = useState(null);
  const [emailReqLoading, setEmailReqLoading] = useState(false);
  const [emailSendLoading, setEmailSendLoading] = useState(false);
  const [emailReqResult, setEmailReqResult] = useState(null);
  const [emailMissing, setEmailMissing] = useState(null);
  const [emailTemplateConfigured, setEmailTemplateConfigured] = useState(false);
  const [dupAlertLoading, setDupAlertLoading] = useState(false);
  const [dupAlertResult, setDupAlertResult] = useState(null);
  const [stornoLoading, setStornoLoading] = useState(false);
  const [stornoResult, setStornoResult] = useState(null);
  const [bdayLoading, setBdayLoading] = useState(false);
  const [bdayResult, setBdayResult] = useState(null);
  const [bdayStatus, setBdayStatus] = useState(null);

  const fetchBirthdayVideoStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/birthday/template-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setBdayStatus(await res.json());
    } catch (_) { /* noop */ }
  };

  const setupBirthdayVideoTemplate = async () => {
    setBdayLoading(true);
    setBdayResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/birthday/create-template`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setBdayResult({ ok: true, msg: `✅ Geburtstags-Video-Vorlage „geburtstag_video_v1" bei Meta eingereicht (Status: ${data.status || 'PENDING'}). Sobald genehmigt, wird das Video auch außerhalb des 24h-Fensters zuverlässig zugestellt.` });
        fetchBirthdayVideoStatus();
      } else {
        setBdayResult({ ok: false, msg: `❌ ${data.detail || data.error || 'Fehler beim Einreichen der Vorlage.'}` });
      }
    } catch (e) {
      setBdayResult({ ok: false, msg: `❌ ${e.message}` });
    } finally {
      setBdayLoading(false);
    }
  };

  const setupInvoiceCancelledTemplate = async () => {
    setStornoLoading(true);
    setStornoResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/setup/invoice-cancelled-template`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const st = data.status === 'EXISTS' ? 'existierte bereits' : (data.status || 'PENDING');
        setStornoResult({ ok: true, msg: `✅ Storno-Vorlage „${data.name}" bei Meta eingereicht (${st}). Sobald genehmigt, wird die Storno-Info auch außerhalb des 24h-Fensters zuverlässig per WhatsApp zugestellt (bis dahin Fallback: freier Text im 24h-Fenster).` });
        try { fetchTemplates(); } catch (_) { /* noop */ }
      } else {
        setStornoResult({ ok: false, msg: `❌ ${data.detail || 'Fehler beim Einreichen der Vorlage.'}` });
      }
    } catch (e) {
      setStornoResult({ ok: false, msg: `❌ ${e.message}` });
    } finally {
      setStornoLoading(false);
    }
  };

  const setupDuplicateAlertTemplate = async () => {
    setDupAlertLoading(true);
    setDupAlertResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/templates/create-duplicate-alert`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const st = data.status === 'EXISTS' ? 'existierte bereits' : (data.status || 'PENDING');
        setDupAlertResult({ ok: true, msg: `✅ Duplikat-Alarm-Vorlage „${data.name}" bei Meta eingereicht (${st}). Sobald genehmigt, werden dir Doppel-Kunden auch außerhalb des 24h-Fensters per WhatsApp mit Ja/Nein-Buttons vorgeschlagen.` });
        try { fetchTemplates(); } catch (_) { /* noop */ }
      } else {
        setDupAlertResult({ ok: false, msg: `❌ ${data.detail || 'Fehler beim Einreichen der Vorlage.'}` });
      }
    } catch (e) {
      setDupAlertResult({ ok: false, msg: `❌ ${e.message}` });
    } finally {
      setDupAlertLoading(false);
    }
  };

  const fetchEmailCollectionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/email-collection/count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEmailMissing(data.count);
        setEmailTemplateConfigured(!!data.template_configured);
      }
    } catch (_) { /* noop */ }
  };

  const setupEmailRequestTemplate = async () => {
    setEmailReqLoading(true);
    setEmailReqResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/templates/create-email-request`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const st = data.status === 'EXISTS' ? 'existierte bereits' : (data.status || 'PENDING');
        setEmailReqResult({ ok: true, msg: `✅ E-Mail-Nachtrag-Vorlage „${data.name}" bei Meta eingereicht (${st}). Sobald sie genehmigt ist, kannst du sie an Kunden ohne E-Mail senden.` });
        setEmailTemplateConfigured(true);
        try { fetchTemplates(); } catch (_) { /* noop */ }
      } else {
        setEmailReqResult({ ok: false, msg: `❌ ${data.detail || 'Fehler beim Einreichen der Vorlage.'}` });
      }
    } catch (e) {
      setEmailReqResult({ ok: false, msg: `❌ ${e.message}` });
    } finally {
      setEmailReqLoading(false);
    }
  };

  const sendEmailRequests = async () => {
    if (!window.confirm(`E-Mail-Nachtrag-Nachricht jetzt an ${emailMissing ?? 0} Kund:innen ohne E-Mail-Adresse senden?`)) return;
    setEmailSendLoading(true);
    setEmailReqResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/email-collection/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setEmailReqResult({ ok: true, msg: `✅ Versand abgeschlossen: ${data.sent} gesendet, ${data.failed} fehlgeschlagen (von ${data.total_missing} ohne E-Mail).` });
        fetchEmailCollectionStatus();
      } else {
        setEmailReqResult({ ok: false, msg: `❌ ${data.detail || 'Versand fehlgeschlagen.'}` });
      }
    } catch (e) {
      setEmailReqResult({ ok: false, msg: `❌ ${e.message}` });
    } finally {
      setEmailSendLoading(false);
    }
  };

  const setupTeamAlertTemplate = async () => {
    setTeamAlertLoading(true);
    setTeamAlertResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/templates/create-team-alert`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const st = data.status === 'EXISTS' ? 'existierte bereits' : (data.status || 'PENDING');
        setTeamAlertResult({ ok: true, msg: `✅ Team-Alarm-Vorlage „${data.name}" bei Meta eingereicht (${st}) und in den Admin-KI-Einstellungen aktiviert.` });
        try { fetchTemplates(); } catch (_) { /* noop */ }
      } else {
        setTeamAlertResult({ ok: false, msg: `❌ ${data.detail || 'Fehler beim Einreichen der Vorlage.'}` });
      }
    } catch (e) {
      setTeamAlertResult({ ok: false, msg: `❌ ${e.message}` });
    } finally {
      setTeamAlertLoading(false);
    }
  };
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    category: 'UTILITY',
    language: 'en_US',  // Default auf Englisch wegen Meta Sperrfrist
    components: []
  });
  const [bodyText, setBodyText] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [hasHeader, setHasHeader] = useState(false);
  const [hasFooter, setHasFooter] = useState(false);
  const [buttons, setButtons] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Beispielwerte für Variablen
  const [bodyExamples, setBodyExamples] = useState(['']);
  const [headerExamples, setHeaderExamples] = useState(['']);
  const [showTemplateExamples, setShowTemplateExamples] = useState(false);
  
  // Edit dialog state
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(null);
  const [localTemplates, setLocalTemplates] = useState({});  // Stores edited templates locally
  
  // Bulk upload state
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState({ current: 0, total: 0, results: [] });
  
  // Bulk delete state
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({ current: 0, total: 0, results: [] });
  
  // Version state - starts at 4, can be incremented
  const [templateVersion, setTemplateVersion] = useState(6);
  
  // AI Bulk Edit state
  const [showAIEditor, setShowAIEditor] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);

  useEffect(() => {
    fetchTemplates();
    fetchEmailCollectionStatus();
    fetchBirthdayVideoStatus();
    fetchWabaStatus();
  }, []);

  // Eigene Vorlagen nur mit eigener WhatsApp-Nummer (WABA). Status vom Katalog.
  const fetchWabaStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/catalog`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setWabaConfigured(!!d.waba_configured);
      }
    } catch (_) { /* noop */ }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Use XMLHttpRequest to avoid rrweb-recorder interference
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${BACKEND_URL}/api/whatsapp/templates`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          let data;
          try {
            data = JSON.parse(xhr.responseText);
          } catch (e) {
            data = null;
          }
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, data, status: xhr.status });
        };
        xhr.onerror = () => reject(new Error('Netzwerkfehler'));
        xhr.send();
      });

      if (result.ok && result.data) {
        setTemplates(result.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  // 06/2026 Whitelist: Vorlage für die Mobile-App zum manuellen Versand freigeben/sperren.
  const toggleAppVisible = (name, nextVal) => {
    const token = localStorage.getItem('token');
    // Optimistisches Update
    setTemplates((prev) => prev.map((t) => (t.name === name ? { ...t, app_visible: nextVal } : t)));
    const xhr = new XMLHttpRequest();
    xhr.open('PATCH', `${BACKEND_URL}/api/whatsapp/templates/${encodeURIComponent(name)}/app-visibility`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    const revert = () =>
      setTemplates((prev) => prev.map((t) => (t.name === name ? { ...t, app_visible: !nextVal } : t)));
    xhr.onload = () => {
      if (!(xhr.status >= 200 && xhr.status < 300)) revert();
    };
    xhr.onerror = revert;
    xhr.send(JSON.stringify({ visible: nextVal }));
  };
  
  // Handle editing a template
  const handleEditTemplate = (tpl) => {
    // Check if we have local edits for this template
    const localVersion = localTemplates[tpl.id];
    setEditingTemplate(localVersion || tpl);
  };
  
  // Handle saving template locally
  const handleSaveTemplate = (updatedTemplate) => {
    setLocalTemplates(prev => ({
      ...prev,
      [updatedTemplate.id]: updatedTemplate
    }));
    setEditingTemplate(null);
    alert('✅ Template lokal gespeichert! Klicken Sie auf "Zu Meta hochladen" um es bei WhatsApp zu registrieren.');
  };
  
  // Build components for Meta API from template data
  const buildComponentsFromTemplate = (templateToUpload) => {
    const components = [];
    
    // Header (if present)
    if (templateToUpload.hasHeader) {
      if (templateToUpload.headerType === 'TEXT' && templateToUpload.headerText) {
        components.push({
          type: 'HEADER',
          format: 'TEXT',
          text: templateToUpload.headerText
        });
      } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(templateToUpload.headerType)) {
        components.push({
          type: 'HEADER',
          format: templateToUpload.headerType,
          example: {
            header_handle: [templateToUpload.headerMediaUrl || 'https://example.com/placeholder']
          }
        });
      }
    }
    
    // Body (required)
    // META API RULE: Text cannot start or end with a variable (even with punctuation after it)
    let bodyText = (templateToUpload.body || '').trim();
    
    // Check if text ends with a variable (possibly followed by punctuation only)
    if (bodyText.match(/\{\{\d+\}\}[.!?,;:]*$/)) {
      // If text ends with a variable (+ optional punctuation), add meaningful text
      bodyText = bodyText.replace(/([.!?,;:]*)$/, '') + '. See you soon!';
    }
    
    // Check if text starts with a variable
    if (bodyText.match(/^\{\{\d+\}\}/)) {
      // If text starts with a variable, add "Hi there! " before it
      bodyText = 'Hi there! ' + bodyText;
    }
    
    const bodyComponent = {
      type: 'BODY',
      text: bodyText
    };
    if (templateToUpload.example && templateToUpload.example.length > 0) {
      bodyComponent.example = {
        body_text: [templateToUpload.example]
      };
    }
    components.push(bodyComponent);
    
    // Footer (if present)
    if (templateToUpload.hasFooter && templateToUpload.footerText) {
      components.push({
        type: 'FOOTER',
        text: templateToUpload.footerText
      });
    }
    
    // Buttons (if present)
    if (templateToUpload.buttons && templateToUpload.buttons.length > 0) {
      const validButtons = templateToUpload.buttons.filter(b => b.text && b.text.trim());
      if (validButtons.length > 0) {
        components.push({
          type: 'BUTTONS',
          buttons: validButtons.map(btn => {
            const buttonObj = {
              type: btn.type,
              text: btn.text
            };
            
            if (btn.type === 'URL' && btn.url) {
              buttonObj.url = btn.url;
              // If URL contains variables, add example (required by Meta API)
              if (btn.url.includes('{{')) {
                // Use provided example or generate a default one
                const exampleValue = btn.url_example || 'example_value_123';
                buttonObj.example = [exampleValue];
              }
            }
            
            if (btn.type === 'PHONE_NUMBER' && btn.phone_number) {
              buttonObj.phone_number = btn.phone_number;
            }
            
            return buttonObj;
          })
        });
      }
    }
    
    return components;
  };
  
  // Handle direct upload to Meta from card
  const handleUploadToMeta = async (tpl) => {
    const templateToUpload = localTemplates[tpl.id] || tpl;
    
    if (!window.confirm(`Template "${templateToUpload.name}" zu Meta hochladen?\n\nDas Template wird zur Prüfung eingereicht und ist nach Genehmigung einsatzbereit.`)) {
      return;
    }
    
    setUploadingTemplate(tpl.id);
    
    try {
      const token = localStorage.getItem('token');
      const templateName = templateToUpload.name.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      
      // Build components including buttons
      const components = buildComponentsFromTemplate(templateToUpload);
      
      // Use XMLHttpRequest to avoid rrweb-recorder interference with Response.clone()
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/whatsapp/templates/create`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          let data;
          try {
            data = JSON.parse(xhr.responseText);
          } catch (e) {
            data = { detail: xhr.responseText || 'Unbekannter Fehler' };
          }
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, data, status: xhr.status });
        };
        xhr.onerror = () => reject(new Error('Netzwerkfehler'));
        xhr.send(JSON.stringify({
          name: templateName,
          category: templateToUpload.category,
          language: templateToUpload.language,
          components: components,
          allow_category_change: true
        }));
      });
      
      if (result.ok) {
        alert(`✅ Erfolgreich!\n\nTemplate "${templateName}" wurde zu Meta hochgeladen.\nStatus: ${result.data.status || 'PENDING'}\n\nDas Template erscheint in wenigen Minuten in der Liste oben.`);
        fetchTemplates();  // Refresh the list
      } else {
        let errorMsg = result.data.detail || result.data.error?.message || 'Unbekannter Fehler';
        
        if (typeof errorMsg === 'object') {
          errorMsg = JSON.stringify(errorMsg);
        }
        
        if (errorMsg.includes('bereits') || errorMsg.includes('exist') || errorMsg.includes('already exists')) {
          errorMsg = `Ein Template mit dem Namen "${templateName}" existiert bereits.`;
        } else if (errorMsg.includes('4 weeks')) {
          errorMsg = `Meta Sperrfrist! Wählen Sie eine andere Sprache oder warten Sie 4 Wochen.`;
        }
        
        alert(`❌ Fehler beim Hochladen:\n\n${errorMsg}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(`❌ Netzwerkfehler: ${error.message}`);
    } finally {
      setUploadingTemplate(null);
    }
  };
  
  // Get template with local edits applied
  const getTemplateWithEdits = (tpl) => {
    return localTemplates[tpl.id] || tpl;
  };

  // Bulk upload all templates to Meta
  const handleBulkUpload = async () => {
    if (!window.confirm(`Alle ${templateExamples.length} Templates zu Meta hochladen?\n\n⚠️ Wichtig:\n• Bereits existierende Templates werden übersprungen\n• Der Vorgang kann einige Minuten dauern\n• Neue Templates müssen von Meta genehmigt werden`)) {
      return;
    }
    
    setBulkUploading(true);
    setBulkUploadProgress({ current: 0, total: templateExamples.length, results: [] });
    
    const results = [];
    const token = localStorage.getItem('token');
    
    for (let i = 0; i < templateExamples.length; i++) {
      const tpl = templateExamples[i];
      const templateToUpload = localTemplates[tpl.id] || tpl;
      const templateName = templateToUpload.name.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      
      setBulkUploadProgress(prev => ({ ...prev, current: i + 1 }));
      
      try {
        const components = buildComponentsFromTemplate(templateToUpload);
        
        const result = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${BACKEND_URL}/api/whatsapp/templates/create`);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.onload = () => {
            let data;
            try {
              data = JSON.parse(xhr.responseText);
            } catch (e) {
              data = { detail: xhr.responseText || 'Unbekannter Fehler' };
            }
            resolve({ ok: xhr.status >= 200 && xhr.status < 300, data, status: xhr.status });
          };
          xhr.onerror = () => reject(new Error('Netzwerkfehler'));
          xhr.send(JSON.stringify({
            name: templateName,
            category: templateToUpload.category,
            language: templateToUpload.language,
            components: components,
            allow_category_change: true
          }));
        });
        
        if (result.ok) {
          results.push({ name: templateName, success: true, status: result.data.status || 'PENDING' });
        } else {
          let errorMsg = result.data.detail || result.data.error?.message || 'Unbekannter Fehler';
          if (typeof errorMsg === 'object') errorMsg = JSON.stringify(errorMsg);
          
          // Check for "already exists" - this is OK
          const alreadyExists = errorMsg.includes('bereits') || errorMsg.includes('exist') || errorMsg.includes('already exists');
          results.push({ 
            name: templateName, 
            success: false, 
            error: alreadyExists ? 'Existiert bereits' : errorMsg,
            skipped: alreadyExists
          });
        }
      } catch (error) {
        results.push({ name: templateName, success: false, error: error.message });
      }
      
      setBulkUploadProgress(prev => ({ ...prev, results: [...results] }));
      
      // Small delay between requests to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setBulkUploading(false);
    
    // Show summary
    const successful = results.filter(r => r.success).length;
    const skipped = results.filter(r => r.skipped).length;
    const failed = results.filter(r => !r.success && !r.skipped).length;
    
    alert(`📊 Bulk Upload abgeschlossen!\n\n✅ Erfolgreich: ${successful}\n⏭️ Übersprungen (existiert): ${skipped}\n❌ Fehlgeschlagen: ${failed}\n\nDie neuen Templates erscheinen nach Meta-Genehmigung in der Liste oben.`);
    
    fetchTemplates();
  };

  const createTemplate = async () => {
    setIsSubmitting(true);
    setSubmitSuccess(false);
    try {
      // Build components array
      const components = [];

      // Add header if enabled
      if (hasHeader && headerText.trim()) {
        const headerVariables = extractVariables(headerText);
        const headerComponent = {
          type: 'HEADER',
          format: 'TEXT',
          text: headerText
        };
        
        // Header example format: ["value1"] not [["value1"]]
        if (headerVariables.length > 0) {
          headerComponent.example = {
            header_text: headerVariables.map((_, i) => `Beispiel ${i + 1}`)
          };
        }
        
        components.push(headerComponent);
      }

      // Add body (required)
      if (!bodyText.trim()) {
        alert('Body-Text ist erforderlich!');
        return;
      }

      const bodyVariables = extractVariables(bodyText);
      const bodyComponent = {
        type: 'BODY',
        text: bodyText
      };

      if (bodyVariables.length > 0) {
        bodyComponent.example = {
          body_text: [bodyVariables.map((_, i) => `Beispiel ${i + 1}`)]
        };
      }

      components.push(bodyComponent);

      // Add footer if enabled
      if (hasFooter && footerText.trim()) {
        components.push({
          type: 'FOOTER',
          text: footerText
        });
      }

      // Add buttons if any
      if (buttons.length > 0) {
        components.push({
          type: 'BUTTONS',
          buttons: buttons
        });
      }

      // Validate template name (lowercase, alphanumeric, underscores only)
      const templateName = newTemplate.name.toLowerCase().replace(/[^a-z0-9_]/g, '_');

      const token = localStorage.getItem('token');
      
      // Use XMLHttpRequest to avoid rrweb-recorder interference
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/whatsapp/templates/create`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          let data;
          try {
            data = JSON.parse(xhr.responseText);
          } catch (e) {
            data = { detail: xhr.responseText || 'Unbekannter Fehler' };
          }
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, data, status: xhr.status });
        };
        xhr.onerror = () => reject(new Error('Netzwerkfehler'));
        xhr.send(JSON.stringify({
          name: templateName,
          category: newTemplate.category,
          language: newTemplate.language,
          components: components,
          allow_category_change: false
        }));
      });

      if (result.ok) {
        setSubmitSuccess(true);
        // Kurz warten, damit der Benutzer den Erfolg sieht
        setTimeout(() => {
          setShowCreateDialog(false);
          resetForm();
          setSubmitSuccess(false);
          fetchTemplates();
        }, 2000);
      } else {
        // Show detailed error message from Meta if available
        let errorMsg = result.data.detail || 'Unbekannter Fehler';
        
        // Check if it's a duplicate template error
        if (errorMsg.includes('bereits') || errorMsg.includes('exist')) {
          errorMsg = `❌ Ein Template mit diesem Namen existiert bereits!\n\nBitte verwenden Sie einen anderen Namen, z.B.:\n- ${newTemplate.name}_2024\n- ${newTemplate.name}_neu\n- ${newTemplate.name}_v2`;
        }
        // Check for language deletion cooldown error
        else if (errorMsg.includes('gel\u00f6scht') || errorMsg.includes('4 weeks') || errorMsg.includes('2388023')) {
          errorMsg = `⚠️ Meta WhatsApp Sperrfrist aktiv!\n\nSie haben kürzlich ein deutsches Template gelöscht.\nMeta sperrt neue deutsche Templates für 4 Wochen.\n\n💡 LÖSUNG:\n1. Wählen Sie eine andere Sprache (z.B. English US)\n2. Oder warten Sie 4 Wochen\n\nAktuell gewählte Sprache: ${newTemplate.language}`;
        }
        
        alert(errorMsg);
      }
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Fehler beim Erstellen des Templates: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteTemplate = async (templateName) => {
    if (!window.confirm(`Template "${templateName}" wirklich löschen?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Use XMLHttpRequest to avoid rrweb-recorder interference
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('DELETE', `${BACKEND_URL}/api/whatsapp/templates/${templateName}`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status });
        xhr.onerror = () => reject(new Error('Netzwerkfehler'));
        xhr.send();
      });

      if (result.ok) {
        alert('✅ Template gelöscht');
        fetchTemplates();
      } else {
        alert('❌ Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  // Bulk delete all APPROVED templates
  const handleBulkDelete = async () => {
    const approvedTemplates = templates.filter(t => t.status === 'APPROVED');
    
    if (approvedTemplates.length === 0) {
      alert('Keine genehmigten (APPROVED) Templates zum Löschen vorhanden.');
      return;
    }
    
    if (!window.confirm(`⚠️ ACHTUNG: ${approvedTemplates.length} genehmigte Templates löschen?\n\nDiese Aktion kann NICHT rückgängig gemacht werden!\n\nFolgende Templates werden gelöscht:\n${approvedTemplates.slice(0, 10).map(t => '• ' + t.name).join('\n')}${approvedTemplates.length > 10 ? '\n... und ' + (approvedTemplates.length - 10) + ' weitere' : ''}`)) {
      return;
    }
    
    // Double confirmation for safety
    if (!window.confirm('🚨 LETZTE WARNUNG 🚨\n\nSind Sie ABSOLUT sicher?\n\nAlle genehmigten WhatsApp-Templates werden bei Meta gelöscht!')) {
      return;
    }
    
    setBulkDeleting(true);
    setBulkDeleteProgress({ current: 0, total: approvedTemplates.length, results: [] });
    
    const results = [];
    const token = localStorage.getItem('token');
    
    for (let i = 0; i < approvedTemplates.length; i++) {
      const template = approvedTemplates[i];
      
      setBulkDeleteProgress(prev => ({ ...prev, current: i + 1 }));
      
      try {
        const result = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('DELETE', `${BACKEND_URL}/api/whatsapp/templates/${template.name}`);
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.onload = () => resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status });
          xhr.onerror = () => reject(new Error('Netzwerkfehler'));
          xhr.send();
        });
        
        if (result.ok) {
          results.push({ name: template.name, success: true });
        } else {
          results.push({ name: template.name, success: false, error: 'Löschung fehlgeschlagen' });
        }
      } catch (error) {
        results.push({ name: template.name, success: false, error: error.message });
      }
      
      setBulkDeleteProgress(prev => ({ ...prev, results: [...results] }));
      
      // Small delay between requests
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    setBulkDeleting(false);
    
    // Show summary
    const successful = results.filter(r => r.success).length;
    const failed = results.filter(r => !r.success).length;
    
    alert(`🗑️ Bulk-Löschung abgeschlossen!\n\n✅ Erfolgreich gelöscht: ${successful}\n❌ Fehlgeschlagen: ${failed}`);
    
    fetchTemplates();
  };

  const extractVariables = (text) => {
    const matches = text.match(/\{\{\d+\}\}/g);
    return matches || [];
  };

  const getBodyText = (template) => {
    if (!template || !Array.isArray(template.components)) return '';
    const bodyComponent = template.components.find((c) => c.type === 'BODY');
    return bodyComponent && typeof bodyComponent.text === 'string' ? bodyComponent.text : '';
  };

  const resetForm = () => {
    setNewTemplate({
      name: '',
      category: 'UTILITY',
      language: 'en_US',  // Default auf Englisch wegen Meta Sperrfrist
      components: []
    });
    setBodyText('');
    setHeaderText('');
    setFooterText('');
    setHasHeader(false);
    setHasFooter(false);
    setButtons([]);
    setIsSubmitting(false);
    setSubmitSuccess(false);
  };

  const addButton = (type) => {
    const newButton = {
      type: type,
      text: ''
    };

    if (type === 'PHONE_NUMBER') {
      newButton.phone_number = '';
    } else if (type === 'URL') {
      newButton.url = '';
    }

    setButtons([...buttons, newButton]);
  };

  const updateButton = (index, field, value) => {
    const updatedButtons = [...buttons];
    updatedButtons[index][field] = value;
    setButtons(updatedButtons);
  };

  const removeButton = (index) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'PENDING':
        return <Clock className="text-yellow-500" size={20} />;
      case 'REJECTED':
        return <XCircle className="text-red-500" size={20} />;
      case 'PAUSED':
        return <AlertCircle className="text-orange-500" size={20} />;
      default:
        return <Clock className="text-gray-400" size={20} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'PAUSED':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get template name with current version
  const getVersionedName = (baseName) => {
    // Replace _v4, _v5, etc. with current version, or add version if not present
    const nameWithoutVersion = baseName.replace(/_v\d+$/, '');
    return `${nameWithoutVersion}_v${templateVersion}`;
  };

  // Template-Vorlagen für Meta - VOLLSTÄNDIGE LISTE mit Buttons & Header Support
  // Note: Names use _v4 as base, but will be replaced with current templateVersion when uploading
  const templateExamplesBase = [
    // ===== TERMINE =====
    {
      id: 'terminbestätigung',
      name: 'gebuchtertermin_v6',
      title: '✅ Terminbestätigung',
      description: 'Bestätigt einen neu gebuchten Termin',
      category: 'UTILITY',
      language: 'de',
      trigger: 'appointment_created',
      body: 'Hallo {{1}}, dein Termin wurde gebucht! 📅\n\n📆 Datum: {{2}}\n🕐 Uhrzeit: {{3}}\n🏋️ Service: {{4}}\n📍 Studio: {{5}}\n\nWir freuen uns auf dich!\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Datum', 'Uhrzeit', 'Service', 'Studio'],
      example: ['Max', '15.12.2024', '14:00', 'EMS Training', 'StrongFit Wetzikon'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Zur App',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app'
        }
      ]
    },
    {
      id: 'terminerinnerung',
      name: 'terminerinnerung_v6',
      title: '📅 Terminerinnerung',
      description: 'Erinnert X Stunden vor dem Termin',
      category: 'UTILITY',
      language: 'de',
      trigger: 'appointment_upcoming',
      body: 'Hallo {{1}}, nicht vergessen! ⏰\n\nDein Termin steht an:\n\n📅 Datum: {{2}}\n🕐 Uhrzeit: {{3}}\n🏋️ Service: {{4}}\n📍 Studio: {{5}}\n\nWir freuen uns auf dich!\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Datum', 'Uhrzeit', 'Service', 'Studio'],
      example: ['Max', '15.12.2024', '14:00', 'EMS Training', 'StrongFit Wetzikon'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Zur App',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app'
        }
      ]
    },
    {
      id: 'terminaenderung',
      name: 'terminaenderung_v6',
      title: '🔄 Terminänderung',
      description: 'Informiert über Terminänderung',
      category: 'UTILITY',
      language: 'de',
      trigger: 'appointment_changed',      body: 'Hallo {{1}}, dein Termin wurde verschoben! 🔄\n\n❌ Alter Termin: {{2}}\n✅ Neuer Termin: {{3}}\n🏋️ Service: {{4}}\n📍 Studio: {{5}}\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Alter Termin', 'Neuer Termin', 'Service', 'Studio'],
      example: ['Max', '13.02.2026 07:30', '14.02.2026 09:30', 'EMS Training', 'StrongFit Wetzikon'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Zur App',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app'
        }
      ]
    },
    {
      id: 'terminstornierung',
      name: 'terminstornierung_v6',
      title: '❌ Terminstornierung',
      description: 'Bestätigt die Stornierung eines Termins',
      category: 'UTILITY',
      language: 'de',
      trigger: 'appointment_cancelled',
      body: 'Hallo {{1}}, dein Termin wurde storniert. ❌\n\n📅 Datum: {{2}}\n🕐 Uhrzeit: {{3}}\n🏋️ Service: {{4}}\n📍 Studio: {{5}}\n\nWir hoffen, dich bald wieder zu sehen!\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Datum', 'Uhrzeit', 'Service', 'Studio'],
      example: ['Max', '15.12.2024', '14:00', 'EMS Training', 'StrongFit Wetzikon'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Neuen Termin buchen',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app'
        }
      ]
    },
    {
      id: 'serienbuchung',
      name: 'serienbuchung_v6',
      title: '📋 Serienbuchung bestätigt',
      description: 'Bestätigt eine Serienbuchung (mehrere Termine)',
      category: 'UTILITY',
      language: 'de',
      trigger: 'series_created',
      body: 'Hallo {{1}}, deine Serienbuchung ist bestätigt! 🎉\n\n🏋️ Service: {{2}}\n📅 Serie: Jeweils {{3}} um {{4}} Uhr\n📆 Erster Termin: {{5}}\n📍 Studio: {{6}}\n\nAlle Termine findest du in der App.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Service', 'Wochentag', 'Uhrzeit', 'Erstes Datum', 'Studio'],
      example: ['Max', 'EMS Training', 'Montag', '14:00', 'Mo, 15.12.2024', 'StrongFit Wetzikon'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Termine ansehen',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app'
        }
      ]
    },
    {
      id: 'serie_storniert',
      name: 'serie_storniert_v6',
      title: '❌ Serie storniert',
      description: 'Benachrichtigt den Kunden wenn eine Serie storniert wurde',
      category: 'UTILITY',
      language: 'de',
      trigger: 'series_cancelled',
      body: 'Hallo {{1}}, deine Serie wurde storniert. ❌\n\n🏋️ Service: {{2}}\n📅 Serie: Jeweils {{3}} um {{4}} Uhr\n📍 Studio: {{5}}\n\nBei Fragen sind wir gerne für dich da.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Service', 'Wochentag', 'Uhrzeit', 'Studio'],
      example: ['Max', 'EMS Training', 'Montag', '07:00', 'StrongFit Wetzikon'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Zur App',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app'
        }
      ]
    },
    {
      id: 'warteliste_eingetragen',
      name: 'warteliste_eingetragen_v1',
      title: '📋 Warteliste - Bestätigung beim Eintragen',
      description: 'Bestätigt dem Kunden, dass er erfolgreich auf der Warteliste steht',
      category: 'UTILITY',
      language: 'de',
      trigger: 'waitlist_joined',
      body: 'Hallo {{1}} 👋\n\nDu stehst jetzt auf der Warteliste für *{{2}}*.\n\n📅 Tage: {{3}}\n🕐 Tageszeit: {{4}}\n📍 Studio: {{5}}\n\nSobald ein Termin frei wird, melden wir uns sofort bei dir.\n\nSportliche Grüsse 💪',
      variables: ['Name', 'Service', 'Tage', 'Tageszeit', 'Studio'],
      example: ['Max', 'Kryosauna', 'Mo, Mi', 'Vormittag', 'StrongFit Wetzikon'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'URL',
          text: 'Zur App',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app'
        }
      ]
    },
    {
      id: 'warteliste_slot',
      name: 'warteliste_slot_v7',
      title: '🔔 Warteliste - Slot verfügbar (One-Click)',
      description: 'Benachrichtigt Wartelisten-Kunden wenn ein Termin frei wird. Klick führt direkt zur Buchungs-Bestätigung ohne Login.',
      category: 'UTILITY',
      language: 'de',
      trigger: 'waitlist_slot_available',
      body: 'Hallo {{1}}, gute Nachrichten! 🎉\n\nEin Platz für *{{2}}* ist frei geworden!\n📆 {{3}} um {{4}} Uhr\n📍 Studio: {{5}}\n\nMit einem Klick verbindlich buchen — ohne Login. Wer zuerst klickt, bekommt den Termin!\n\nSportliche Grüsse 💪',
      variables: ['Name', 'Service', 'Datum', 'Uhrzeit', 'Studio'],
      example: ['Max', 'EMS Training', '15.01.2026', '14:00', 'StrongFit Wetzikon'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Jetzt buchen',
          url_type: 'query',
          url_base: 'https://app.strongfit.ch/waitlist/quick-confirm',
          query_param: 't',
          url: 'https://app.strongfit.ch/waitlist/quick-confirm?t={{1}}',
          url_example: 'cb382439-1fc6-4518.abc1234567890def'
        }
      ]
    },
    {
      id: 'warteliste_autogebucht',
      name: 'warteliste_autogebucht_v7',
      title: '✅ Warteliste - Automatisch gebucht',
      description: 'Bestätigung wenn ein Kunde automatisch von der Warteliste gebucht wurde',
      category: 'UTILITY',
      language: 'de',
      trigger: 'waitlist_auto_booked',
      body: 'Hallo {{1}}, gute Nachrichten! 🎉\n\nDein Termin wurde automatisch gebucht:\n\n🏋️ Service: {{2}}\n📅 Datum: {{3}}\n🕐 Uhrzeit: {{4}}\n📍 Studio: {{5}}\n\nDu warst auf der Warteliste und ein Platz wurde frei. Falls du nicht kommen kannst, storniere bitte rechtzeitig.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Service', 'Datum', 'Uhrzeit', 'Studio'],
      example: ['Max', 'EMS Training', '20.01.2026', '09:00', 'StrongFit Wetzikon'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Zur App',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app'
        }
      ]
    },
    {
      id: 'warteliste_folgetag',
      name: 'warteliste_folgetag_v7',
      title: '📋 Warteliste - Folgetag Bestätigung',
      description: 'Fragt den Kunden, ob er an zwei aufeinanderfolgenden Tagen trainieren möchte',
      category: 'UTILITY',
      language: 'de',
      trigger: 'waitlist_consecutive_day',
      body: 'Hallo {{1}}, ein Termin ist verfügbar! 📅\n\n🏋️ Service: {{2}}\n📅 Datum: {{3}}\n🕐 Uhrzeit: {{4}}\n📍 Studio: {{5}}\n\nDa du bereits einen Termin am Vortag hast, haben wir diesen für dich reserviert.\n\n⏰ Bitte bestätige innerhalb von 4 Stunden.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Service', 'Datum', 'Uhrzeit', 'Studio'],
      example: ['Max', 'EMS Training', '12.02.2026', '14:00', 'StrongFit Wetzikon'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Bestätigen',
          url_type: 'query',
          url_base: 'https://app.strongfit.ch/waitlist-confirm',
          query_param: 'token',
          url: 'https://app.strongfit.ch/waitlist-confirm?token={{1}}',
          url_example: 'abc123-xyz-456'
        }
      ]
    },
    
    // ===== RECHNUNGEN & ZAHLUNGEN =====
    {
      id: 'rechnung',
      name: 'rechnung_neu_v6',
      title: '📄 Neue Rechnung',
      description: 'Informiert über eine neue Rechnung mit PDF-Link und Zahlungslink',
      category: 'UTILITY',
      language: 'de',
      trigger: 'invoice_created',
      body: 'Hallo {{1}}, du hast eine neue Rechnung erhalten. 📄\n\n📋 Rechnung: {{2}}\n💰 Betrag: {{3}}\n\n📥 PDF herunterladen:\n{{4}}\n\nFür eine fristgerechte Zahlung bedanken wir uns schon im Voraus.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Rechnungsnummer', 'Betrag', 'PDF-Link'],
      example: ['Max', 'RG-2026-001', 'CHF 150.00', 'https://app.strongfit.ch/invoice/RG-2026-001.pdf'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Jetzt bezahlen',
          url_type: 'query',
          url_base: 'https://strongfit.payrexx.com/',
          query_param: 'payment',
          url: 'https://strongfit.payrexx.com/?payment={{1}}',
          url_example: 'abc123def456ghi789'
        }
      ]
    },
    {
      id: 'zahlungserinnerung',
      name: 'zahlungerinnerung_v6',
      title: '💳 Zahlungserinnerung',
      description: 'Erinnert an offene Rechnungen',
      category: 'UTILITY',
      language: 'de',
      trigger: 'manual_invoice_reminder',
      body: 'Hallo {{1}}, deine Rechnung {{2}} über {{3}} ist noch offen.\n\n📅 Fällig bis: {{4}}\n\nFür eine zeitnahe Zahlung bedanken wir uns im Voraus.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Rechnungsnummer', 'Betrag', 'Fälligkeitsdatum'],
      example: ['Max', 'RG-2024-001', 'CHF 150.00', '20.12.2024'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Jetzt bezahlen',
          url_type: 'query',
          url_base: 'https://strongfit.payrexx.com/',
          query_param: 'payment',
          url: 'https://strongfit.payrexx.com/?payment={{1}}',
          url_example: 'abc123def456'
        }
      ]
    },
    {
      id: 'bezahlterechnung',
      name: 'zahlungsbest_tigt_v6',
      title: '✅ Bezahlte Rechnung',
      description: 'Bestätigung für bezahlte Rechnungen',
      category: 'UTILITY',
      language: 'de',
      trigger: 'invoice_paid',
      body: 'Hallo {{1}}, vielen Dank für deine Zahlung! ✅\n\n📋 Rechnung: {{2}}\n💰 Betrag: {{3}}\n💳 Zahlungsart: {{4}}\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Rechnungsnummer', 'Betrag', 'Zahlungsart'],
      example: ['Max', 'RG-2024-001', 'CHF 150.00', 'Kreditkarte'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Zur App',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app'
        }
      ]
    },
    {
      id: 'zahlungfehlgeschlagen',
      name: 'zahlungfehlgeschlagen_v6',
      title: '⚠️ Zahlung fehlgeschlagen',
      description: 'Informiert über fehlgeschlagene Kartenzahlung',
      category: 'UTILITY',
      language: 'de',
      trigger: 'payment_failed',
      body: 'Hallo {{1}}, deine Zahlung konnte leider nicht durchgeführt werden. ⚠️\n\nBitte aktualisiere deine Zahlungsmethode.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name'],
      example: ['Max'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Karte aktualisieren',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app?to=zahlungsmethode'
        }
      ]
    },
    {
      id: 'paymentlink',
      name: 'payment_link_v6',
      title: '💳 Karte hinterlegen',
      description: 'Aufforderung zur Hinterlegung einer Zahlungsmethode',
      category: 'UTILITY',
      language: 'de',
      trigger: 'payment_link_sent',
      body: 'Hallo {{1}}, bitte hinterlege eine Zahlungsmethode für zukünftige Buchungen. 💳\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name'],
      example: ['Max'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Karte hinterlegen',
          url_type: 'query',
          url_base: 'https://strongfit.payrexx.com/',
          query_param: 'payment',
          url: 'https://strongfit.payrexx.com/?payment={{1}}',
          url_example: 'abc123def456'
        }
      ]
    },
    {
      id: 'mietrechnung',
      name: 'mietrechnung_v6',
      title: '🏠 Mietrechnung',
      description: 'Monatliche Mietrechnung für Raummieter',
      category: 'UTILITY',
      language: 'de',
      trigger: 'rent_invoice_created',
      body: 'Hallo {{1}}, du erhältst deine Mietrechnung für {{2}}.\n\n📄 Betrag: {{3}}\n📋 Rechnung: {{4}}\n📅 Fällig bis: {{5}}\n\nFür eine fristgerechte Zahlung bedanken wir uns im Voraus.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Mieter-Name', 'Zeitraum', 'Betrag', 'Rechnungsnummer', 'Fälligkeitsdatum'],
      example: ['Max', 'Januar 2026', 'CHF 500.00', 'MIETE-2026-0001', '31.01.2026'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Rechnung ansehen',
          url_type: 'query',
          url_base: 'https://app.strongfit.ch/invoices/view',
          query_param: 'id',
          url: 'https://app.strongfit.ch/invoices/view?id={{1}}',
          url_example: 'MIETE-2026-0001'
        }
      ]
    },
    
    // ===== PUNKTE & ABOS =====
    {
      id: 'punktewarnung',
      name: 'punktwarnung_v6',
      title: '⚠️ Punkte-Warnung',
      description: 'Warnt bei niedrigem Punktestand',
      category: 'UTILITY',
      language: 'de',
      trigger: 'points_below_threshold',
      body: 'Hoi {{1}}, habe soeben gesehen, dass dein Punktepaket bald aufgebraucht ist. Du hast noch {{2}} Punkte. ⚠️\n\nIch sende dir gleich deine Trainings-Historie als PDF.\nDürfen wir dir ein neues Punktepaket aufladen?\n\nWenn du willst, kannst du hier selbst nachladen: {{3}}\n\nSportliche Grüsse\nAlex',
      variables: ['Name', 'Punktestand', 'Link'],
      example: ['Max', '50', 'https://strongfit.app.link/punkte'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'In der App aufladen',
          url_type: 'static',
          url: 'https://strongfit.app.link/punkte'
        }
      ]
    },
    {
      id: 'abowarnung',
      name: 'abowarnung_v6',
      title: '🔔 Abo-Credits Warnung',
      description: 'Warnt bei wenigen verbleibenden Abo-Credits',
      category: 'UTILITY',
      language: 'de',
      trigger: 'subscription_credits_below_threshold',
      body: 'Hoi {{1}}, habe soeben gesehen, dass dein Abo bald aufgebraucht ist. Du hast noch {{2}} Sessions übrig! 🔔\n\nIch sende dir gleich deine Trainings-Historie als PDF.\nDürfen wir dir ein neues Abo aufladen?\n\nWenn du willst, kannst du hier direkt verlängern: {{3}}\n\nSportliche Grüsse\nAlex',
      variables: ['Name', 'Verbleibende Sessions', 'Link'],
      example: ['Max', '2', 'https://strongfit.app.link/abo'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'In der App verlängern',
          url_type: 'static',
          url: 'https://strongfit.app.link/abo'
        }
      ]
    },
    {
      id: 'aboerneuert',
      name: 'aboerneuert_v6',
      title: '🔄 Abo automatisch erneuert',
      description: 'Bestätigt die automatische Abo-Erneuerung',
      category: 'UTILITY',
      language: 'de',
      trigger: 'subscription_renewed',
      body: 'Hallo {{1}}, dein {{2}} Abo wurde automatisch verlängert! 🔄\n\n✅ Neuer Stand: {{3}} Sessions\n💰 Abgebuchter Betrag: {{4}}\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Abo-Name', 'Neue Sessions', 'Betrag'],
      example: ['Max', 'EMS 10er', '10', 'CHF 450.00'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Zur App',
          url_type: 'static',
          url: 'https://strongfit.app.link'
        }
      ]
    },
    {
      id: 'autoreload_fehlgeschlagen',
      name: 'autoreload_fehlgeschlagen_v6',
      title: '⚠️ Auto-Reload fehlgeschlagen',
      description: 'Informiert wenn automatische Abo-Abbuchung fehlschlägt',
      category: 'UTILITY',
      language: 'de',
      trigger: 'subscription_reload_failed',
      body: 'Hallo {{1}}, die automatische Abbuchung für dein {{2}} Abo konnte nicht durchgeführt werden. ⚠️\n\nBitte prüfe deine Zahlungsmethode.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Abo-Name'],
      example: ['Max', 'EMS 10er'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'In der App prüfen',
          url_type: 'static',
          url: 'https://strongfit.app.link/zahlungsmethode'
        }
      ]
    },
    {
      id: 'training_charge_failed',
      name: 'training_charge_failed_v6',
      title: '💳 Training-Abbuchung fehlgeschlagen',
      description: 'Informiert wenn eine Trainings-Rechnung nicht automatisch von der hinterlegten Karte belastet werden konnte',
      category: 'UTILITY',
      language: 'de',
      trigger: 'training_charge_failed',
      body: 'Hallo {{1}}, leider konnten wir die Rechnung {{2}} über {{3}} nicht automatisch von deiner Karte abbuchen. ⚠️\n\nBitte bezahle manuell oder aktualisiere deine Zahlungsmethode. Wir versuchen es automatisch nächste Woche erneut.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Rechnungsnummer', 'Betrag'],
      example: ['Alex', 'RG-20260429-AB12', 'CHF 45.00'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'URL',
          text: 'Jetzt manuell bezahlen',
          url_type: 'dynamic',
          url: 'https://strongfit.payrexx.com/?payment={{1}}',
          example: 'abc123def'
        },
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        }
      ]
    },
    {
      id: 'punkte_aufgeladen',
      name: 'punkte_aufgeladen_v6',
      title: '✅ Punkte aufgeladen',
      description: 'Bestätigt erfolgreiche Punkte-Aufladung',
      category: 'UTILITY',
      language: 'de',
      trigger: 'points_added',
      body: 'Hallo {{1}}, dein Konto wurde erfolgreich aufgeladen! ✅\n\n➕ Neue Punkte: {{2}}\n💰 Neuer Stand: {{3}} Punkte\n\nVielen Dank!\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Aufgeladene Punkte', 'Neuer Stand'],
      example: ['Max', '100', '250'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Zur App',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app'
        }
      ]
    },
    {
      id: 'chf_guthaben',
      name: 'chf_guthaben_v6',
      title: '💰 CHF-Guthaben gutgeschrieben',
      description: 'Bestätigt, dass dem Kunden CHF als Guthaben gutgeschrieben wurden',
      category: 'UTILITY',
      language: 'de',
      trigger: 'credit_added',
      body: 'Hallo {{1}}, dir wurden *CHF {{2}}* als Guthaben gutgeschrieben. ✅\n\nGrund: {{3}}\nDein neues Guthaben: *CHF {{4}}*\n\nDas Guthaben wird automatisch bei deiner nächsten Buchung verrechnet.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Gutgeschriebener Betrag', 'Grund', 'Neuer Saldo'],
      example: ['Max', '50.00', 'Kulanz', '150.00'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Zur App',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app'
        }
      ]
    },

    // ===== KUNDEN =====
    {
      id: 'willkommen',
      name: 'willkommen_v6',
      title: '👋 Willkommen',
      description: 'Begrüsst neue Kunden',
      category: 'MARKETING',
      language: 'de',
      trigger: 'customer_registered',
      body: 'Hallo {{1}}, herzlich willkommen bei StrongFit! 👋\n\nWir freuen uns, dich als Kunden begrüssen zu dürfen. Bei Fragen sind wir gerne für dich da.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name'],
      example: ['Max'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Zur App',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app'
        }
      ]
    },
    {
      id: 'erstertermin',
      name: 'erstertermin_v6',
      title: '🎉 Nach erstem Termin',
      description: 'Feedback-Anfrage nach erstem Besuch',
      category: 'MARKETING',
      language: 'de',
      trigger: 'first_appointment_completed',
      body: 'Hallo {{1}}, vielen Dank für deinen ersten Besuch bei uns! 🎉\n\nWie war dein {{2}}? Wir hoffen, es hat dir gefallen!\n\nBuche gleich deinen nächsten Termin.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Service'],
      example: ['Max', 'EMS Training'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Nächsten Termin buchen',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app?to=buchen'
        }
      ]
    },
    {
      id: 'reaktivierung',
      name: 'reaktivierung_v6',
      title: '💪 Konto reaktiviert',
      description: 'Begrüsst zurückkehrende Kunden',
      category: 'MARKETING',
      language: 'de',
      trigger: 'account_reactivation',
      body: 'Hallo {{1}}, schön dich wiederzusehen! 💪\n\nDein Konto wurde reaktiviert. Wir freuen uns auf deinen nächsten Besuch!\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name'],
      example: ['Max'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Termin buchen',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app?to=buchen'
        }
      ]
    },
    {
      id: 'reengagement_30d',
      name: 'reaktivierung_30d_v1',
      title: '🔁 Re-Engagement 30 Tage',
      description: 'Erinnerung nach 30 Tagen ohne Termin',
      category: 'MARKETING',
      language: 'de',
      trigger: 'reengagement_30d',
      body: 'Hoi {{1}} 👋\n\ndein letzter Termin liegt schon {{2}} Tage zurück.\nWenn du wieder durchstarten möchtest, freuen wir uns, dich bald bei uns zu sehen 💪\n\nHier kannst du dir direkt deinen nächsten Termin buchen:\n{{3}}\n\nLiebe Grüsse\nAlex',
      variables: ['Name', 'Tage ohne Termin', 'Portal-Link'],
      example: ['Max', '30', 'https://app.strongfit.ch/portal'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Termin buchen',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app?to=buchen'
        }
      ]
    },
    {
      id: 'reengagement_60d',
      name: 'reaktivierung_60d_v1',
      title: '🎁 Re-Engagement 60 Tage (10%)',
      description: 'Erinnerung nach 60 Tagen mit Rabattcode',
      category: 'MARKETING',
      language: 'de',
      trigger: 'reengagement_60d',
      body: 'Hoi {{1}} 👋\n\ndein letzter Termin liegt schon {{2}} Tage zurück.\nAls kleines Dankeschön erhältst du {{3}}% Rabatt auf dein nächstes Abo.\nDein Rabattcode: {{4}} (gültig 30 Tage)\n\nHier kannst du dir direkt deinen nächsten Termin buchen:\n{{5}}\n\nLiebe Grüsse\nAlex',
      variables: ['Name', 'Tage ohne Termin', 'Rabatt %', 'Rabattcode', 'Portal-Link'],
      example: ['Max', '60', '10', 'RE60-ABC123', 'https://app.strongfit.ch/portal'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Termin buchen',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app?to=buchen'
        }
      ]
    },
    // ===== WIN-BACK (3-Stage Reactivation Funnel) =====
    // Werden vom Win-Back-Scheduler (siehe backend/routes/winback_routes.py)
    // gefeuert, wenn ein Abo ausläuft und der Kunde nicht erneuert.
    // Templates landen in der Automation-Page unter "Win-Back Stufe 1/2/3".
    // Kategorie MARKETING ist hier richtig (kein OTP, keine Utility) —
    // Reaktivierungs-Mails sind per Meta-Definition Marketing.
    {
      id: 'winback_stage_1',
      name: 'winback_welcome_v1',
      title: '🤍 Win-Back Stufe 1 (1 Tag)',
      description: 'Freundliche Erinnerung 1 Tag nach Abo-Ende — kein Rabatt, nur Brücke zurück',
      category: 'MARKETING',
      language: 'de',
      trigger: 'winback_stage_1',
      body: 'Hoi {{1}} 👋\n\ndein StrongFit-Abo ist gestern ausgelaufen.\nWir haben gemerkt, dass du noch nicht verlängert hast — alles okay bei dir?\n\nFalls du einfach eine Pause brauchst: kein Problem. Falls du Fragen oder Wünsche hast, melde dich gerne direkt — wir finden eine Lösung, die zu deinem Alltag passt.\n\nWir freuen uns, wenn du bald wieder bei uns trainierst 💪\n\nLiebe Grüsse\nAlex',
      variables: ['Name'],
      example: ['Max'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Termin buchen',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app?to=buchen'
        }
      ]
    },
    {
      id: 'winback_stage_2',
      name: 'winback_reminder_v1',
      title: '⏰ Win-Back Stufe 2 (7 Tage)',
      description: 'Zweite Erinnerung nach einer Woche — etwas direkter, Trainings-Mehrwert betonen',
      category: 'MARKETING',
      language: 'de',
      trigger: 'winback_stage_2',
      body: 'Hoi {{1}},\n\nschon eine Woche ohne Training — kennst du das Gefühl? Die Routine geht schnell verloren.\n\nDeine letzten Erfolge sollen nicht verfliegen. Wir halten dir gerne deinen gewohnten Slot wieder frei, sag einfach Bescheid wann es dir passt.\n\nKlick unten auf "Termin buchen" oder ruf direkt durch — wir kümmern uns persönlich um dich.\n\nLiebe Grüsse\nDein StrongFit Team',
      variables: ['Name'],
      example: ['Max'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Termin buchen',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app?to=buchen'
        }
      ]
    },
    {
      id: 'winback_stage_3',
      name: 'winback_last_chance_v1',
      title: '🎁 Win-Back Stufe 3 (15 Tage, mit Rabatt)',
      description: 'Letzte Stufe nach 15 Tagen mit Rabattcode — Win-Back-Funnel mit konkretem Anreiz',
      category: 'MARKETING',
      language: 'de',
      trigger: 'winback_stage_3',
      body: 'Hoi {{1}},\n\nzwei Wochen sind ohne Training vergangen. Wir möchten dir gerne den Wiedereinstieg leichter machen 🎁\n\nMit diesem Rabattcode bekommst du {{2}}% auf dein nächstes Abo:\nCode: {{3}} (gültig {{4}} Tage)\n\nBuch dir direkt deinen nächsten Termin oder ruf an, wenn du Fragen hast.\n\nWir freuen uns auf dich 💪\nAlex',
      variables: ['Name', 'Rabatt %', 'Rabattcode', 'Gültig (Tage)'],
      example: ['Max', '5', 'BACK5-XYZ123', '7'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Termin buchen',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app?to=buchen'
        }
      ]
    },
    // ===== RESCHEDULE OFFER (Slot-Reservation Bot-Vorschlag) =====
    // Wird vom WhatsApp-Webhook + Reschedule-Helper (Iter 309) ausgelöst,
    // wenn ein Kunde im Chat eine Verschiebungs-Anfrage stellt.
    // Bot prüft Wunsch-Tage und schlägt pro Tag den besten 1 Slot vor.
    // Beide Slots werden je 3h (konfigurierbar) reserviert.
    // Hinweis: Aktuell wird die Nachricht als Plain-Text gesendet — sobald
    // dieses Template bei Meta APPROVED ist, kann der Bot stattdessen
    // das Template mit Quick-Reply-Buttons verwenden.
    {
      id: 'reschedule_offer',
      name: 'reschedule_offer_wa_v1',
      title: '🔄 Termin-Verschiebung Angebot',
      description: 'Bietet 2 alternative Termin-Slots an, wenn Kunde im Chat verschieben möchte',
      category: 'UTILITY',
      language: 'de',
      trigger: 'reschedule_offer',
      body: 'Hoi {{1}} 👋\n\nklar, kein Problem! Ich habe für dich diese 2 Termine kurz reserviert:\n\n1️⃣ {{2}}\n2️⃣ {{3}}\n\nDie Reservierung läuft in {{4}} ab. Wähle einfach einen aus, ich aktiviere ihn dann sofort für dich und sage den alten Termin automatisch ab.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Vorname', 'Slot-1 (z.B. Di 16.06. 09:00)', 'Slot-2 (z.B. Do 18.06. 08:30)', 'Verbleibende Zeit (z.B. 3 Stunden)'],
      example: ['Sandra', 'Di 16.06. 09:00 mit Alex', 'Do 18.06. 08:30 mit Sarah', '3 Stunden'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'QUICK_REPLY',
          text: 'Option 1 wählen'
        },
        {
          type: 'QUICK_REPLY',
          text: 'Option 2 wählen'
        },
        {
          type: 'QUICK_REPLY',
          text: 'Andere Tage bitte'
        }
      ]
    },
    // ===== TRIAL FOLLOW-UP =====
    // Wird vom Trial-Follow-Up-Scheduler (siehe backend/services/trial_followup_service.py)
    // ausgelöst, wenn ein Kunde nach einem Probetraining nicht erneut bucht.
    // Bietet einen kostenlosen 2. Versuch bei einem ANDEREN Trainer an.
    // Kategorie MARKETING ist hier richtig (Promo-Angebot, kein OTP/Utility).
    {
      id: 'trial_followup',
      name: 'trial_followup_wa_v1',
      title: '🎁 Trial Follow-Up (Probetraining-Nachfass)',
      description: 'Bietet nach einem Probetraining kostenlos einen 2. Versuch bei einem anderen Trainer an',
      category: 'MARKETING',
      language: 'de',
      trigger: 'trial_followup',
      body: 'Hoi {{1}} 👋\n\ndein {{2}} bei uns war erst der Anfang! 🎁 Wir möchten dir gerne einen kostenlosen 2. Versuch schenken – diesmal bei einem anderen Coach, damit du den perfekten Fit für dich findest.\n\nVerfügbare Trainer:innen: {{3}}\n\nBuch dir hier direkt deinen Gratis-Termin:\n{{4}}\n\nWir freuen uns auf dich 💪\nSportliche Grüsse\nDein StrongFit Team 🏋️',
      variables: ['Vorname', 'Service-Name', 'Andere Trainer', 'Gutschein-Link'],
      example: ['Max', 'EMS Probetraining', 'Sarah, Tom', 'https://app.strongfit.ch/trial?code=ABC123'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'URL',
          text: 'Jetzt kostenlos buchen',
          url_type: 'dynamic',
          url: 'https://app.strongfit.ch/trial?code={{1}}',
          example: ['ABC123']
        },
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        }
      ]
    },
    {
      id: 'feedback',
      name: 'feedback_v6',
      title: '⭐ Feedback anfragen',
      description: 'Bittet um Feedback nach Termin',
      category: 'MARKETING',
      language: 'de',
      trigger: 'training_completed',
      body: 'Hallo {{1}}, wie war dein {{2}} am {{3}}? ⭐\n\nWir würden uns über dein Feedback freuen!\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Service', 'Datum'],
      example: ['Max', 'EMS Training', '15.12.2024'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Feedback geben',
          url_type: 'static',
          url: 'https://g.page/r/CQjvJWL0TzWZEAg/review'
        }
      ]
    },
    {
      id: 'geburtstag',
      name: 'geburtstag_v6',
      title: '🎂 Geburtstag',
      description: 'Glückwünsche zum Geburtstag',
      category: 'MARKETING',
      language: 'de',
      trigger: 'birthday',
      body: 'Hallo {{1}}, alles Gute zum Geburtstag! 🎂🎉\n\nAls kleines Geschenk schenken wir dir {{2}}.\n\nGeniesse deinen Tag!\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Geschenk'],
      example: ['Max', '10% Rabatt auf deine nächste Buchung'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Geschenk einlösen',
          url_type: 'static',
          url: 'https://app.strongfit.ch/app?to=buchen'
        }
      ]
    },
    
    // ===== MARKETING =====
    {
      id: 'kampagne',
      name: 'kampagne_v6',
      title: '📢 Kampagne',
      description: 'Allgemeine Marketing-Kampagne',
      category: 'MARKETING',
      language: 'de',
      trigger: 'campaign',
      body: '{{1}}',
      variables: ['Kampagnen-Text'],
      example: ['Hallo Max, entdecke unsere neuen Angebote!'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Mehr erfahren',
          url_type: 'static',
          url: 'https://www.strongfit.ch'
        }
      ]
    },
    {
      id: 'promotion',
      name: 'promotion_v6',
      title: '🎁 Sonderangebot',
      description: 'Bewirbt Sonderaktionen',
      category: 'MARKETING',
      language: 'de',
      trigger: 'promotion',
      body: 'Hallo {{1}}, nur für kurze Zeit: {{2}}! 🎁\n\n📅 Gültig bis: {{3}}\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Angebot', 'Ablaufdatum'],
      example: ['Max', '20% Rabatt auf alle EMS-Pakete', '31.12.2024'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Angebot sichern',
          url_type: 'static',
          url: 'https://app.strongfit.ch/portal/angebote'
        }
      ]
    },
    {
      id: 'event',
      name: 'event_v6',
      title: '🎉 Event-Einladung',
      description: 'Lädt zu Events ein',
      category: 'MARKETING',
      language: 'de',
      trigger: 'event_announcement',
      body: 'Hallo {{1}}, wir laden dich herzlich ein! 🎉\n\n📌 {{2}}\n📅 {{3}} um {{4}}\n\nPlätze sind begrenzt - melde dich jetzt an!\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Event-Name', 'Datum', 'Uhrzeit'],
      example: ['Max', 'EMS Workshop', '20.12.2024', '18:00'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'PHONE_NUMBER',
          text: 'Anrufen',
          phone_number: '+41796969899'
        },
        {
          type: 'URL',
          text: 'Anmelden',
          url_type: 'static',
          url: 'https://app.strongfit.ch/events'
        }
      ]
    },
    
    // ===== PUNKTE & ABO TEILEN =====
    {
      id: 'invitation',
      name: 'sharing_invitation_v6',
      title: '🎁 Einladung: Punkte/Abo teilen',
      description: 'Jemand möchte seine Punkte oder Rechnungen mit dem Empfänger teilen',
      category: 'UTILITY',
      language: 'de',
      trigger: 'sharing_invitation',
      body: 'Hallo {{1}}, {{2}} möchte {{3}} mit dir teilen! 🎁\n\nÖffne die StrongFit-App, um die Einladung anzunehmen oder abzulehnen.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name (Empfänger)', 'Name (Einlader)', 'Was geteilt wird'],
      example: ['Max', 'Anna Muster', 'Punkte und Rechnungen'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'URL',
          text: 'Zur App',
          url: 'https://app.strongfit.ch/app?to=meine-pakete/teilen'
        }
      ]
    },
    {
      id: 'invitation_sent',
      name: 'sharing_invitation_sent_v6',
      title: '✉️ Einladung versendet',
      description: 'Bestätigung an den Einlader, dass die Einladung verschickt wurde',
      category: 'UTILITY',
      language: 'de',
      trigger: 'sharing_invitation_sent',
      body: 'Hallo {{1}}, deine Einladung an {{2}} ({{3}}) wurde erfolgreich versendet. ✉️\n\nDu erhältst eine Nachricht, sobald {{2}} antwortet.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name (Einlader)', 'Name (Empfänger)', 'Was geteilt wird'],
      example: ['Anna', 'Max Muster', 'Punkte und Rechnungen'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'URL',
          text: 'Zur App',
          url: 'https://app.strongfit.ch/app?to=meine-pakete/teilen'
        }
      ]
    },
    {
      id: 'response_accepted',
      name: 'sharing_response_accepted_v6',
      title: '✅ Einladung angenommen',
      description: 'Benachrichtigt den Einlader, wenn der Empfänger die Einladung angenommen hat',
      category: 'UTILITY',
      language: 'de',
      trigger: 'sharing_response_accepted',
      body: 'Hallo {{1}}, {{2}} hat deine Einladung angenommen! ✅\n\nAb sofort werden {{3}} geteilt.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name (Einlader)', 'Name (Empfänger)', 'Was geteilt wird'],
      example: ['Anna', 'Max Muster', 'Punkte und Rechnungen'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'URL',
          text: 'Zur App',
          url: 'https://app.strongfit.ch/app?to=meine-pakete/teilen'
        }
      ]
    },
    {
      id: 'response_rejected',
      name: 'sharing_response_rejected_v6',
      title: '❌ Einladung abgelehnt',
      description: 'Benachrichtigt den Einlader, wenn der Empfänger die Einladung abgelehnt hat',
      category: 'UTILITY',
      language: 'de',
      trigger: 'sharing_response_rejected',
      body: 'Hallo {{1}}, {{2}} hat deine Einladung leider abgelehnt. ❌\n\nDu kannst jederzeit eine neue Einladung an jemand anderen senden.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name (Einlader)', 'Name (Empfänger)'],
      example: ['Anna', 'Max Muster'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'URL',
          text: 'Zur App',
          url: 'https://app.strongfit.ch/app?to=meine-pakete/teilen'
        }
      ]
    },

    // ===== FEIERTAGE & FERIEN =====
    {
      id: 'holiday_reminder',
      name: 'holiday_reminder_v6',
      title: '📅 Erinnerung: Feiertag voraus',
      description: 'Informiert Kunden eine Woche vor einem Feiertag über betroffene Termine',
      category: 'UTILITY',
      language: 'de',
      trigger: 'holiday_reminder',
      body: 'Hallo {{1}},\n\nin einer Woche ist {{2}} ({{3}}). Du hast {{4}} Termin(e) in dieser Zeit. 📅\n\nFalls du verschieben oder stornieren möchtest, öffne die StrongFit-App.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Feiertag', 'Datum', 'Anzahl Termine'],
      example: ['Max', 'Auffahrt', '21.05.2026', '2'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'URL',
          text: 'Zur App',
          url: 'https://app.strongfit.ch/app?to=termine'
        }
      ]
    },
    {
      id: 'vacation_reminder',
      name: 'vacation_reminder_v6',
      title: '🏖️ Erinnerung: Schulferien voraus',
      description: 'Informiert Kunden 2-3 Wochen vor Schulferien (Zürich) über betroffene Termine',
      category: 'UTILITY',
      language: 'de',
      trigger: 'vacation_reminder',
      body: 'Hallo {{1}},\n\nbald beginnen die {{2}} (ab {{3}}). Du hast {{4}} Termin(e) in dieser Zeit. 🏖️\n\nFalls du verschieben oder stornieren möchtest, öffne die StrongFit-App.\n\nSportliche Grüsse 💪\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Ferien', 'Startdatum', 'Anzahl Termine'],
      example: ['Max', 'Sommerferien', '11.07.2026', '8'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'URL',
          text: 'Zur App',
          url: 'https://app.strongfit.ch/app?to=termine'
        }
      ]
    },

    // ===== WIEDERAKTIVIERUNG (Cart Abandonment) =====
    {
      id: 'abandonment_recovery',
      name: 'abandonment_recovery_v6',
      title: '🎁 Neukunde: 10% Gutschein',
      description: 'Erinnert Neukunden nach 4 Wochen ohne Kauf mit personalisiertem 10%-Gutschein',
      category: 'MARKETING',
      language: 'de',
      trigger: 'abandonment_recovery',
      body: 'Hallo {{1}}, schön dich an Bord zu haben! 💪\n\nAls Willkommens-Geschenk schenken wir dir {{2}}% Rabatt auf deinen ersten Kauf:\n\n🎁 *{{3}}*\n\nGültig {{4}} Tage, einmalig einlösbar in der StrongFit-App.\n\nSportliche Grüsse\nDein StrongFit Team 🏋️',
      variables: ['Name', 'Rabatt-Prozent', 'Gutschein-Code', 'Gültigkeit (Tage)'],
      example: ['Max', '10', 'SF-AB12CD34', '30'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'URL',
          text: 'Zur App',
          url: 'https://app.strongfit.ch/app?to=shop'
        }
      ]
    },

    // ===== AUTHENTIFIZIERUNG (Passwordless Login) =====
    // Dieses Template hat KEINE Version (`noVersion: true`), weil Meta
    // Authentication-Templates nur einmal eingereicht werden und sich nicht
    // versionieren wie Utility/Marketing-Templates.
    {
      id: 'otp_login_code',
      name: 'otp_login_code',
      noVersion: true,
      title: '🔐 Login-Code (passwortlos)',
      description: 'Einmal-Code für passwortlosen Login per WhatsApp. Authentication-Kategorie für Meta.',
      category: 'AUTHENTICATION',
      language: 'de',
      trigger: 'otp_login',
      body: 'Hallo {{1}},\n\ndein einmaliger StrongFit Login-Code lautet: *{{2}}*\n\nDer Code ist 5 Minuten gültig. Wenn du diesen Login nicht angefragt hast, ignoriere diese Nachricht.',
      variables: ['Name', 'Code'],
      example: ['Alex', '483921'],
      hasHeader: false,
      hasFooter: false,
      buttons: []
    },
    // ===== PUNKTE-GESCHENK (Welle A) =====
    // Notification an den Empfänger nachdem jemand ihm Punkte verschenkt hat.
    {
      id: 'punkte_geschenk_erhalten',
      name: 'punkte_geschenk_erhalten_v1',
      noVersion: true,  // Eigener Versions-Anker — nutzt v1 fix
      title: '🎁 Punkte als Geschenk erhalten',
      description: 'Benachrichtigung an einen Kunden, wenn ein anderer Kunde ihm Trainings-Punkte geschenkt hat.',
      category: 'UTILITY',
      language: 'de',
      trigger: 'points_gift_received',
      body: '🎁 Hallo {{1}}!\n\nDu hast {{2}} Trainings-Punkte von {{3}} geschenkt bekommen. Sie wurden bereits deinem Konto gutgeschrieben — du kannst sie ab sofort für deine Trainings nutzen.\n\nSportliche Grüsse\nDein StrongFit Team',
      variables: ['Name', 'Punkte', 'Schenker-Name'],
      example: ['Anna', '10', 'Max Mustermann'],
      hasHeader: false,
      hasFooter: false,
      buttons: [
        {
          type: 'URL',
          text: 'Zur App',
          url: 'https://app.strongfit.ch/portal'
        }
      ]
    }
  ];

  // Generate templateExamples with current version number.
  // Templates mit `noVersion: true` (z. B. otp_login_code) behalten den
  // Original-Namen — Authentication-Templates dürfen bei Meta nur einmal
  // existieren und werden nicht versioniert.
  const templateExamples = templateExamplesBase.map(tpl => ({
    ...tpl,
    name: tpl.noVersion ? tpl.name : getVersionedName(tpl.name)
  }));

  // Increment version for all templates
  const handleVersionIncrement = () => {
    const newVersion = templateVersion + 1;
    if (window.confirm(`Version von v${templateVersion} auf v${newVersion} erhöhen?\n\nAlle Template-Namen werden aktualisiert:\n• gebuchtertermin_v${templateVersion} → gebuchtertermin_v${newVersion}\n• terminerinnerung_v${templateVersion} → terminerinnerung_v${newVersion}\n• usw.\n\nDanach können Sie alle alten Templates löschen und die neuen hochladen.`)) {
      setTemplateVersion(newVersion);
      setLocalTemplates({}); // Clear local edits
      alert(`✅ Version auf v${newVersion} erhöht!\n\nAlle ${templateExamplesBase.length} Templates haben jetzt die neue Version.\n\nNächste Schritte:\n1. "Alle APPROVED löschen" klicken\n2. "Alle hochladen" klicken`);
    }
  };

  // AI Bulk Edit Handler
  const handleAIBulkEdit = async () => {
    if (!aiPrompt.trim()) {
      alert('Bitte geben Sie einen Befehl ein.');
      return;
    }
    
    setAiProcessing(true);
    setAiPreview(null);
    
    try {
      const token = localStorage.getItem('token');
      
      // Prepare current templates for AI
      const currentTemplates = templateExamples.map(t => ({
        id: t.id,
        name: t.name,
        title: t.title,
        body: localTemplates[t.id]?.body || t.body,
        buttons: localTemplates[t.id]?.buttons || t.buttons || []
      }));
      
      // Call AI endpoint
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/templates/ai-bulk-edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: aiPrompt,
          templates: currentTemplates
        })
      });
      
      if (!response.ok) {
        throw new Error('KI-Anfrage fehlgeschlagen');
      }
      
      const result = await response.json();
      
      if (result.success && result.modified_templates) {
        setAiPreview(result);
      } else {
        alert('Die KI konnte keine Änderungen vornehmen: ' + (result.message || 'Unbekannter Fehler'));
      }
    } catch (error) {
      console.error('AI bulk edit error:', error);
      alert('❌ Fehler bei der KI-Verarbeitung: ' + error.message);
    } finally {
      setAiProcessing(false);
    }
  };

  // Apply AI Changes
  const applyAIChanges = () => {
    if (!aiPreview || !aiPreview.modified_templates) return;
    
    const newLocalTemplates = { ...localTemplates };
    
    aiPreview.modified_templates.forEach(modified => {
      const original = templateExamples.find(t => t.id === modified.id);
      if (original) {
        newLocalTemplates[modified.id] = {
          ...original,
          ...newLocalTemplates[modified.id],
          body: modified.body,
          buttons: modified.buttons || original.buttons
        };
      }
    });
    
    setLocalTemplates(newLocalTemplates);
    setAiPreview(null);
    setAiPrompt('');
    setShowAIEditor(false);
    
    alert(`✅ ${aiPreview.modified_templates.length} Templates wurden aktualisiert!\n\nDie Änderungen wurden lokal gespeichert. Klicken Sie auf "Alle hochladen", um sie zu Meta zu übertragen.`);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('In Zwischenablage kopiert!');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Lade Templates...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">WhatsApp Templates</h1>
          <p className="text-sm text-gray-600 mt-1">
            Erstellen und verwalten Sie Nachrichten-Templates für automatische Benachrichtigungen
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={setupTeamAlertTemplate}
            disabled={teamAlertLoading}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60"
            data-testid="setup-team-alert-template-btn"
            title="Erstellt die Vorlage für kurzfristige Absage-Alarme ans Team und reicht sie bei Meta ein"
          >
            {teamAlertLoading ? <Loader2 size={18} className="animate-spin" /> : <AlertTriangle size={18} />}
            Team-Alarm-Vorlage einrichten
          </button>
          <button
            onClick={() => setShowCreateDialog(true)}
            disabled={!wabaConfigured}
            data-testid="create-template-btn"
            title={wabaConfigured ? 'Eigene Vorlage erstellen' : 'Eigene Vorlagen nur mit eigener WhatsApp-Nummer möglich'}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={20} />
            Neues Template
          </button>
        </div>
      </div>

      {!wabaConfigured && (
        <div className="mb-6 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="wa-own-number-notice">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <b>Eigene Vorlagen erfordern eine eigene WhatsApp-Nummer.</b> Ohne eigene Nummer stehen die
            fertigen <span className="font-medium">Standard-Vorlagen</span> (unten) zur Verfügung — mit automatisch
            eingesetztem Betriebsnamen. Hinterlege Business-Account-ID &amp; Access-Token in den WhatsApp-Einstellungen,
            um eigene Vorlagen zu erstellen.
          </div>
        </div>
      )}

      <WhatsAppCatalogSection />

      {teamAlertResult && (
        <div
          className={`mb-6 rounded-lg p-3 text-sm ${teamAlertResult.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}
          data-testid="team-alert-result"
        >
          {teamAlertResult.msg}
          {teamAlertResult.ok && (
            <span className="block text-xs mt-1 text-gray-600">
              Hinweis: Meta prüft neue Vorlagen automatisch (meist Minuten bis wenige Stunden). Status siehst du unten in der Liste.
            </span>
          )}
        </div>
      )}

      {/* E-Mail-Nachtrag via WhatsApp (06/2026) */}
      <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid="email-collection-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-lg bg-blue-600 p-2 text-white"><Mail size={20} /></div>
            <div>
              <h3 className="font-semibold text-gray-900">E-Mail-Adressen per WhatsApp nachfragen</h3>
              <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                Kund:innen ohne hinterlegte E-Mail-Adresse (aber mit WhatsApp-Nummer) werden gebeten, ihre E-Mail zu senden.
                Antwortet die Person mit einer E-Mail, wird sie <b>automatisch</b> im Profil gespeichert.
                {emailMissing !== null && (
                  <span className="block mt-1 font-medium text-blue-800" data-testid="email-missing-count">
                    Aktuell ohne E-Mail (mit WhatsApp-Nummer): {emailMissing}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={setupEmailRequestTemplate}
              disabled={emailReqLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
              data-testid="setup-email-request-template-btn"
              title="Erstellt die E-Mail-Nachtrag-Vorlage und reicht sie bei Meta ein"
            >
              {emailReqLoading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
              Vorlage einrichten
            </button>
            <button
              onClick={sendEmailRequests}
              disabled={emailSendLoading || !emailTemplateConfigured}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              data-testid="send-email-requests-btn"
              title={emailTemplateConfigured ? 'Sendet die Nachfrage an alle Kund:innen ohne E-Mail' : 'Bitte zuerst die Vorlage einrichten und von Meta genehmigen lassen'}
            >
              {emailSendLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              Jetzt senden{emailMissing !== null ? ` (${emailMissing})` : ''}
            </button>
          </div>
        </div>
        {emailReqResult && (
          <div
            className={`mt-3 rounded-lg p-3 text-sm ${emailReqResult.ok ? 'bg-white border border-emerald-200 text-emerald-800' : 'bg-white border border-red-200 text-red-800'}`}
            data-testid="email-request-result"
          >
            {emailReqResult.msg}
          </div>
        )}
        <p className="text-xs text-gray-500 mt-2">
          Automatik: Läuft zusätzlich täglich um 10:45 (Cooldown 30 Tage pro Person), sobald die Vorlage genehmigt ist.
        </p>
      </div>

      {/* Duplikat-Alarm-Vorlage (06/2026) — hält die 24h-Regel ein */}
      <div className="mb-6 rounded-xl border border-purple-200 bg-purple-50 p-4" data-testid="duplicate-alert-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-lg bg-purple-600 p-2 text-white"><Users size={20} /></div>
            <div>
              <h3 className="font-semibold text-gray-900">Doppel-Kunden-Alarm (Admin)</h3>
              <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                Meta-Vorlage für die täglichen Duplikat-Vorschläge an deine Admin-Nummer – mit
                <b> Ja/Nein-Buttons</b>. Nur mit genehmigter Vorlage werden die Vorschläge auch
                <b> außerhalb des 24-Stunden-Fensters</b> zugestellt.
              </p>
            </div>
          </div>
          <button
            onClick={setupDuplicateAlertTemplate}
            disabled={dupAlertLoading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-60"
            data-testid="setup-duplicate-alert-template-btn"
          >
            {dupAlertLoading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
            Vorlage einrichten
          </button>
        </div>
        {dupAlertResult && (
          <div
            className={`mt-3 rounded-lg p-3 text-sm ${dupAlertResult.ok ? 'bg-white border border-purple-200 text-purple-800' : 'bg-white border border-red-200 text-red-800'}`}
            data-testid="duplicate-alert-result"
          >
            {dupAlertResult.msg}
          </div>
        )}
        <p className="text-xs text-gray-500 mt-2">
          Admin-Nummer(n) hinterlegst du in den Admin-KI-Einstellungen. Die Vorschlagsliste unter „Kunden → Doppelte Kunden" funktioniert auch ohne Vorlage.
        </p>
      </div>

      {/* Rechnungs-Storno-Vorlage (09/2026) — zuverlässige Storno-Info per WhatsApp */}
      <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="invoice-cancelled-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-lg bg-amber-600 p-2 text-white"><Plus size={20} /></div>
            <div>
              <h3 className="font-semibold text-gray-900">Rechnungs-Storno (Kunde)</h3>
              <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                Meta-Vorlage <b>„rechnung_storniert"</b>, damit ein Kunde bei Storno seiner Rechnung
                auch <b>außerhalb des 24-Stunden-Fensters</b> zuverlässig per WhatsApp informiert wird.
                Ohne Vorlage kommt der freie Text nur an, wenn der Kunde in den letzten 24h geschrieben hat.
              </p>
            </div>
          </div>
          <button
            onClick={setupInvoiceCancelledTemplate}
            disabled={stornoLoading}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-60"
            data-testid="setup-invoice-cancelled-template-btn"
          >
            {stornoLoading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
            Vorlage einrichten
          </button>
        </div>
        {stornoResult && (
          <div
            className={`mt-3 rounded-lg p-3 text-sm ${stornoResult.ok ? 'bg-white border border-amber-200 text-amber-800' : 'bg-white border border-red-200 text-red-800'}`}
            data-testid="invoice-cancelled-result"
          >
            {stornoResult.msg}
          </div>
        )}
      </div>

      {/* Geburtstags-Video-Vorlage (06/2026) — Video-Gruss außerhalb des 24h-Fensters */}
      <div className="mb-6 rounded-xl border border-pink-200 bg-pink-50 p-4" data-testid="birthday-video-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-lg bg-pink-600 p-2 text-white"><Plus size={20} /></div>
            <div>
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                Geburtstags-Video-Gruss 🎂
                {bdayStatus && (
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold border ${
                    bdayStatus.status === 'APPROVED' ? 'bg-green-100 text-green-800 border-green-300' :
                    (bdayStatus.status === 'PENDING' || bdayStatus.status === 'IN_REVIEW') ? 'bg-amber-100 text-amber-800 border-amber-300' :
                    bdayStatus.status === 'REJECTED' ? 'bg-red-100 text-red-800 border-red-300' :
                    'bg-gray-100 text-gray-700 border-gray-300'
                  }`} data-testid="birthday-video-status-badge">
                    {bdayStatus.status === 'APPROVED' ? '✓ Freigegeben' :
                     (bdayStatus.status === 'PENDING' || bdayStatus.status === 'IN_REVIEW') ? '⏳ In Prüfung' :
                     bdayStatus.status === 'REJECTED' ? '✗ Abgelehnt' :
                     '↗ Nicht erstellt'}
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                Meta-Vorlage <b>„geburtstag_video_v1"</b> mit <b>Video-Header</b>. Damit können deine
                Trainer aus der App ein aufgenommenes Geburtstags-Video auch dann senden, wenn der Kunde
                <b> nicht</b> in den letzten 24 Stunden geschrieben hat. Einmalig einrichten – die Freigabe
                durch Meta dauert i.&nbsp;d.&nbsp;R. einige Stunden.
              </p>
            </div>
          </div>
          <button
            onClick={setupBirthdayVideoTemplate}
            disabled={bdayLoading}
            className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-60"
            data-testid="setup-birthday-video-template-btn"
          >
            {bdayLoading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
            Vorlage einrichten
          </button>
        </div>
        {bdayResult && (
          <div
            className={`mt-3 rounded-lg p-3 text-sm ${bdayResult.ok ? 'bg-white border border-pink-200 text-pink-800' : 'bg-white border border-red-200 text-red-800'}`}
            data-testid="birthday-video-result"
          >
            {bdayResult.msg}
          </div>
        )}
      </div>


      {/* Mehrsprachigkeit Phase 4 (06/2026) */}
      <WhatsAppI18nSection />

      {/* Template-Vorlagen Sektion */}
      <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        {/* Status-Übersicht: zeigt wie viele Templates schon bei Meta
            sind. User-Wunsch 05/2026: „Ich sehe nicht welche hochgeladen
            sind und welche nicht." Berechnen wir live aus templateExamples
            (alle möglichen Vorlagen) vs. templates (was bei Meta liegt).
            Wir vergleichen über getVersionedName(), damit es robust gegen
            Versionsnummer-Bumps ist. */}
        {showTemplateExamples && (() => {
          const metaStatusByName = {};
          templates.forEach(t => {
            if (t.name) metaStatusByName[t.name] = t.status;
          });
          const counts = { uploaded: 0, pending: 0, rejected: 0, missing: 0 };
          templateExamples.forEach(tpl => {
            const status = metaStatusByName[tpl.name];
            if (status === 'APPROVED') counts.uploaded++;
            else if (status === 'PENDING' || status === 'IN_REVIEW') counts.pending++;
            else if (status === 'REJECTED') counts.rejected++;
            else counts.missing++;
          });
          return (
            <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
                 data-testid="whatsapp-template-status-summary">
              <div className="bg-green-100 text-green-800 border border-green-300 rounded-md px-3 py-2 text-center font-semibold">
                ✓ Hochgeladen: {counts.uploaded}
              </div>
              <div className="bg-amber-100 text-amber-800 border border-amber-300 rounded-md px-3 py-2 text-center font-semibold">
                ⏳ In Prüfung: {counts.pending}
              </div>
              <div className="bg-orange-100 text-orange-800 border border-orange-300 rounded-md px-3 py-2 text-center font-semibold">
                ↗ Fehlt noch: {counts.missing}
              </div>
              <div className="bg-red-100 text-red-800 border border-red-300 rounded-md px-3 py-2 text-center font-semibold">
                ✗ Abgelehnt: {counts.rejected}
              </div>
            </div>
          );
        })()}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              📋 Template-Vorlagen für Meta
              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                Version {templateVersion}
              </span>
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Kopieren Sie diese Vorlagen und erstellen Sie sie in Ihrem Meta Business Account
            </p>
          </div>
          <div className="flex items-center gap-3">
            {showTemplateExamples && (
              <>
                <button
                  onClick={() => setShowAIEditor(!showAIEditor)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    showAIEditor 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                  }`}
                >
                  <Sparkles size={16} />
                  KI-Editor
                </button>
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 rounded-lg px-3 py-1">
                  <span className="text-sm font-medium text-amber-700">Version:</span>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={templateVersion}
                    onChange={(e) => {
                      const newVersion = parseInt(e.target.value) || 1;
                      if (newVersion >= 1 && newVersion <= 99) {
                        setTemplateVersion(newVersion);
                        setLocalTemplates({});
                      }
                    }}
                    className="w-14 px-2 py-1 text-center font-bold text-amber-700 bg-white border border-amber-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <button
                  onClick={handleVersionIncrement}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-amber-500 text-white hover:bg-amber-600"
                  title="Version um 1 erhöhen"
                >
                  v{templateVersion} → v{templateVersion + 1}
                </button>
                <button
                  onClick={handleBulkUpload}
                  disabled={bulkUploading}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    bulkUploading 
                      ? 'bg-gray-200 text-gray-500 cursor-wait' 
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {bulkUploading ? (
                    <>
                      <Clock size={16} className="animate-spin" />
                      {bulkUploadProgress.current}/{bulkUploadProgress.total}
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      Alle hochladen ({templateExamples.length})
                    </>
                  )}
                </button>
              </>
            )}
            <button
              onClick={() => setShowTemplateExamples(!showTemplateExamples)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {showTemplateExamples ? '▼ Ausblenden' : '▶ Anzeigen'}
            </button>
          </div>
        </div>

        {/* Bulk Upload Progress */}
        {bulkUploading && (
          <div className="mb-4 p-4 bg-white rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Hochladen... {bulkUploadProgress.current} von {bulkUploadProgress.total}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round((bulkUploadProgress.current / bulkUploadProgress.total) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(bulkUploadProgress.current / bulkUploadProgress.total) * 100}%` }}
              />
            </div>
            {bulkUploadProgress.results.length > 0 && (
              <div className="mt-3 max-h-32 overflow-y-auto text-xs">
                {bulkUploadProgress.results.slice(-5).map((r, i) => (
                  <div key={i} className={`flex items-center gap-2 py-1 ${r.success ? 'text-green-600' : r.skipped ? 'text-yellow-600' : 'text-red-600'}`}>
                    {r.success ? '✅' : r.skipped ? '⏭️' : '❌'} {r.name}
                    {r.error && <span className="text-gray-500">- {r.error}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* AI Editor Panel */}
        {showAIEditor && showTemplateExamples && (
          <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="text-purple-600" size={20} />
              <h3 className="font-bold text-purple-900">KI-Massenbearbeitung</h3>
            </div>
            <p className="text-sm text-purple-700 mb-3">
              Beschreiben Sie, was Sie ändern möchten. Die KI wird alle Vorlagen entsprechend anpassen.
            </p>
            
            <div className="space-y-3">
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Beispiele:
• Ändere 'Sportliche Grüsse' zu 'Liebe Grüsse'
• Ersetze die Telefonnummer +41796969899 durch +41791234567
• Ändere alle Links von strongfit.app.link zu meineapp.ch
• Füge am Ende jeder Nachricht '🎯 Dein Fitness-Team' hinzu
• Entferne alle Emojis aus den Nachrichten"
                className="w-full h-32 p-3 border border-purple-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              />
              
              <div className="flex gap-2">
                <button
                  onClick={handleAIBulkEdit}
                  disabled={aiProcessing || !aiPrompt.trim()}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    aiProcessing || !aiPrompt.trim()
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  {aiProcessing ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      KI verarbeitet...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      Änderungen vorschlagen
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowAIEditor(false);
                    setAiPrompt('');
                    setAiPreview(null);
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  Schliessen
                </button>
              </div>
            </div>

            {/* AI Preview */}
            {aiPreview && aiPreview.modified_templates && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-purple-300">
                <h4 className="font-bold text-purple-900 mb-2">
                  ✨ Vorgeschlagene Änderungen ({aiPreview.modified_templates.length} Templates)
                </h4>
                <div className="max-h-64 overflow-y-auto space-y-2 mb-3">
                  {aiPreview.modified_templates.slice(0, 5).map((t, i) => (
                    <div key={i} className="p-2 bg-purple-50 rounded text-xs">
                      <div className="font-medium text-purple-800">{t.title || t.name}</div>
                      <div className="text-purple-600 mt-1 whitespace-pre-line line-clamp-3">{t.body}</div>
                    </div>
                  ))}
                  {aiPreview.modified_templates.length > 5 && (
                    <div className="text-sm text-purple-600 text-center">
                      ... und {aiPreview.modified_templates.length - 5} weitere Templates
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={applyAIChanges}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                  >
                    <CheckCircle size={16} />
                    Änderungen übernehmen
                  </button>
                  <button
                    onClick={() => setAiPreview(null)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
                  >
                    Verwerfen
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 06/2026 App-Freigabe (Whitelist) — immer sichtbar: Nur freigegebene,
            genehmigte Vorlagen erscheinen in der Mobile-App beim Schreiben. */}
        <div className="mb-5 bg-white border border-emerald-200 rounded-lg p-4" data-testid="app-visible-whitelist-panel">
          <h3 className="text-md font-bold text-gray-800 mb-1 flex items-center gap-2">
            📱 In App zum Versand freigeben
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            Nur hier freigegebene (und von Meta genehmigte) Vorlagen erscheinen in der Mobile-App beim Schreiben einer WhatsApp-Nachricht.
          </p>
          {templates.filter((t) => (t.status || '').toUpperCase() === 'APPROVED').length === 0 ? (
            <p className="text-xs text-gray-400 italic">Keine genehmigten Vorlagen vorhanden.</p>
          ) : (
            <>
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={appVisibleSearch}
                  onChange={(e) => setAppVisibleSearch(e.target.value)}
                  placeholder="Vorlage suchen…"
                  className="w-full pl-8 pr-8 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  data-testid="app-visible-search"
                />
                {appVisibleSearch && (
                  <button
                    type="button"
                    onClick={() => setAppVisibleSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="app-visible-search-clear"
                    aria-label="Suche löschen"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                {(() => {
                  // Jede Vorlage nur EINMAL (unabhängig von Sprachvarianten) — Freigabe gilt pro Name.
                  const q = appVisibleSearch.trim().toLowerCase();
                  const seen = new Set();
                  const rows = templates
                    .filter((t) => (t.status || '').toUpperCase() === 'APPROVED')
                    .filter((t) => {
                      if (seen.has(t.name)) return false;
                      seen.add(t.name);
                      return true;
                    })
                    .filter((t) => !q || (t.name || '').toLowerCase().includes(q))
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                  if (rows.length === 0) {
                    return (
                      <p className="text-xs text-gray-400 italic py-2" data-testid="app-visible-no-results">
                        Keine Vorlage gefunden für „{appVisibleSearch}".
                      </p>
                    );
                  }
                  return rows.map((t) => (
                    <div
                      key={t.name}
                      className="flex items-center justify-between gap-3 py-1.5 px-2 rounded hover:bg-gray-50"
                      data-testid={`app-visible-row-${t.name}`}
                    >
                      <div className="min-w-0 flex-1">
                        <code className="text-xs text-gray-800 break-all">{t.name}</code>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleAppVisible(t.name, !t.app_visible)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                          t.app_visible ? 'bg-emerald-500' : 'bg-gray-300'
                        }`}
                        role="switch"
                        aria-checked={!!t.app_visible}
                        data-testid={`app-visible-toggle-${t.name}`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                            t.app_visible ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  ));
                })()}
              </div>
            </>
          )}
        </div>

        {showTemplateExamples && (
          <>
            {/* Termine Section */}
            <div className="mb-4">
              <h3 className="text-md font-bold text-gray-800 mb-3 flex items-center gap-2">
                📅 Termine
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {templateExamples.filter(t => ['appointment_created', 'appointment_upcoming', 'appointment_changed', 'appointment_cancelled', 'appointment_cancellation', 'series_created', 'series_cancelled', 'waitlist_joined', 'waitlist_slot_available', 'waitlist_auto_booked', 'waitlist_consecutive_day', 'reschedule_offer'].includes(t.trigger)).map((tpl) => (
                  <TemplateCard 
                    key={tpl.id} 
                    tpl={getTemplateWithEdits(tpl)} 
                    copyToClipboard={copyToClipboard}
                    onEdit={handleEditTemplate}
                    onUploadToMeta={handleUploadToMeta}
                    uploadingTemplate={uploadingTemplate}
                    metaStatus={(templates.find(t => t.name === tpl.name) || {}).status}
                  />
                ))}
              </div>
            </div>

            {/* Rechnungen & Zahlungen Section */}
            <div className="mb-4">
              <h3 className="text-md font-bold text-gray-800 mb-3 flex items-center gap-2">
                💳 Rechnungen & Zahlungen
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {templateExamples.filter(t => ['invoice_created', 'manual_invoice_reminder', 'invoice_paid', 'payment_confirmed', 'payment_failed', 'payment_link_sent', 'rent_invoice_created', 'training_charge_failed', 'credit_added'].includes(t.trigger)).map((tpl) => (
                  <TemplateCard 
                    key={tpl.id} 
                    tpl={getTemplateWithEdits(tpl)} 
                    copyToClipboard={copyToClipboard}
                    onEdit={handleEditTemplate}
                    onUploadToMeta={handleUploadToMeta}
                    uploadingTemplate={uploadingTemplate}
                    metaStatus={(templates.find(t => t.name === tpl.name) || {}).status}
                  />
                ))}
              </div>
            </div>

            {/* Punkte & Abos Section */}
            <div className="mb-4">
              <h3 className="text-md font-bold text-gray-800 mb-3 flex items-center gap-2">
                ⚡ Punkte & Abos
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {templateExamples.filter(t => ['points_below_threshold', 'subscription_credits_below_threshold', 'subscription_renewed', 'subscription_reload_failed', 'points_added'].includes(t.trigger)).map((tpl) => (
                  <TemplateCard 
                    key={tpl.id} 
                    tpl={getTemplateWithEdits(tpl)} 
                    copyToClipboard={copyToClipboard}
                    onEdit={handleEditTemplate}
                    onUploadToMeta={handleUploadToMeta}
                    uploadingTemplate={uploadingTemplate}
                    metaStatus={(templates.find(t => t.name === tpl.name) || {}).status}
                  />
                ))}
              </div>
            </div>

            {/* Punkte & Abos teilen Section */}
            <div className="mb-4">
              <h3 className="text-md font-bold text-gray-800 mb-3 flex items-center gap-2">
                🤝 Punkte & Abos teilen
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {templateExamples.filter(t => ['sharing_invitation', 'sharing_invitation_sent', 'sharing_response_accepted', 'sharing_response_rejected'].includes(t.trigger)).map((tpl) => (
                  <TemplateCard
                    key={tpl.id}
                    tpl={getTemplateWithEdits(tpl)}
                    copyToClipboard={copyToClipboard}
                    onEdit={handleEditTemplate}
                    onUploadToMeta={handleUploadToMeta}
                    uploadingTemplate={uploadingTemplate}
                    metaStatus={(templates.find(t => t.name === tpl.name) || {}).status}
                  />
                ))}
              </div>
            </div>

            {/* Feiertage & Ferien Section */}
            <div className="mb-4">
              <h3 className="text-md font-bold text-gray-800 mb-3 flex items-center gap-2">
                📅 Feiertage & Schulferien
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {templateExamples.filter(t => ['holiday_reminder', 'vacation_reminder'].includes(t.trigger)).map((tpl) => (
                  <TemplateCard
                    key={tpl.id}
                    tpl={getTemplateWithEdits(tpl)}
                    copyToClipboard={copyToClipboard}
                    onEdit={handleEditTemplate}
                    onUploadToMeta={handleUploadToMeta}
                    uploadingTemplate={uploadingTemplate}
                    metaStatus={(templates.find(t => t.name === tpl.name) || {}).status}
                  />
                ))}
              </div>
            </div>

            {/* Wiederaktivierung / Cart-Abandonment Section */}
            <div className="mb-4">
              <h3 className="text-md font-bold text-gray-800 mb-3 flex items-center gap-2">
                🎁 Wiederaktivierung
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {templateExamples.filter(t => ['abandonment_recovery'].includes(t.trigger)).map((tpl) => (
                  <TemplateCard
                    key={tpl.id}
                    tpl={getTemplateWithEdits(tpl)}
                    copyToClipboard={copyToClipboard}
                    onEdit={handleEditTemplate}
                    onUploadToMeta={handleUploadToMeta}
                    uploadingTemplate={uploadingTemplate}
                    metaStatus={(templates.find(t => t.name === tpl.name) || {}).status}
                  />
                ))}
              </div>
            </div>

            {/* Kunden Section */}
            <div className="mb-4">
              <h3 className="text-md font-bold text-gray-800 mb-3 flex items-center gap-2">
                👥 Kunden
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {templateExamples.filter(t => ['customer_registered', 'first_appointment_completed', 'account_reactivation', 'reengagement_30d', 'reengagement_60d', 'feedback_request', 'birthday', 'winback_stage_1', 'winback_stage_2', 'winback_stage_3', 'trial_followup', 'training_completed', 'points_gift_received'].includes(t.trigger)).map((tpl) => (
                  <TemplateCard 
                    key={tpl.id} 
                    tpl={getTemplateWithEdits(tpl)} 
                    copyToClipboard={copyToClipboard}
                    onEdit={handleEditTemplate}
                    onUploadToMeta={handleUploadToMeta}
                    uploadingTemplate={uploadingTemplate}
                    metaStatus={(templates.find(t => t.name === tpl.name) || {}).status}
                  />
                ))}
              </div>
            </div>

            {/* Marketing Section */}
            <div className="mb-4">
              <h3 className="text-md font-bold text-gray-800 mb-3 flex items-center gap-2">
                📢 Marketing & Kampagnen
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {templateExamples.filter(t => ['campaign', 'promotion', 'event_invitation', 'event_announcement'].includes(t.trigger)).map((tpl) => (
                  <TemplateCard 
                    key={tpl.id} 
                    tpl={getTemplateWithEdits(tpl)} 
                    copyToClipboard={copyToClipboard}
                    onEdit={handleEditTemplate}
                    onUploadToMeta={handleUploadToMeta}
                    uploadingTemplate={uploadingTemplate}
                    metaStatus={(templates.find(t => t.name === tpl.name) || {}).status}
                  />
                ))}
              </div>
            </div>
            
            {/* Kundenportal Section */}
            <div className="mb-4">
              <h3 className="text-md font-bold text-gray-800 mb-3 flex items-center gap-2">
                🔑 Kundenportal
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {templateExamples.filter(t => ['password_reset', 'password_reset_requested'].includes(t.trigger)).map((tpl) => (
                  <TemplateCard 
                    key={tpl.id} 
                    tpl={getTemplateWithEdits(tpl)} 
                    copyToClipboard={copyToClipboard}
                    onEdit={handleEditTemplate}
                    onUploadToMeta={handleUploadToMeta}
                    uploadingTemplate={uploadingTemplate}
                    metaStatus={(templates.find(t => t.name === tpl.name) || {}).status}
                  />
                ))}
              </div>
            </div>

            {/* Authentifizierung / Passwordless Login Section */}
            <div className="mb-4">
              <h3 className="text-md font-bold text-gray-800 mb-3 flex items-center gap-2">
                🔐 Login & Authentifizierung
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {templateExamples.filter(t => ['otp_login'].includes(t.trigger)).map((tpl) => (
                  <TemplateCard
                    key={tpl.id}
                    tpl={getTemplateWithEdits(tpl)}
                    copyToClipboard={copyToClipboard}
                    onEdit={handleEditTemplate}
                    onUploadToMeta={handleUploadToMeta}
                    uploadingTemplate={uploadingTemplate}
                    metaStatus={(templates.find(t => t.name === tpl.name) || {}).status}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 Dieses Template muss bei Meta in der Kategorie <strong>Authentication</strong> eingereicht werden.
                Bis zur Freigabe wird der Code per Email versendet (Fallback).
              </p>
            </div>
          </>
        )}

        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>💡 Anleitung:</strong> Gehen Sie zu <a href="https://business.facebook.com/wa/manage/message-templates/" target="_blank" rel="noopener noreferrer" className="underline">Meta Business Suite → WhatsApp → Message Templates</a>, 
            klicken Sie auf "Create Template", wählen Sie Kategorie "UTILITY", Sprache "English (US)", und fügen Sie den Body-Text ein. 
            Bei Variablen geben Sie die Beispiel-Werte ein.
          </p>
        </div>
      </div>

      {/* Templates Liste Header mit Bulk Delete */}
      {templates.length > 0 && (
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900">
            Bei Meta registrierte Templates ({templates.length})
          </h2>
          <div className="flex items-center gap-3">
            {templates.filter(t => t.status === 'APPROVED').length > 0 && (
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  bulkDeleting 
                    ? 'bg-gray-200 text-gray-500 cursor-wait' 
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {bulkDeleting ? (
                  <>
                    <Clock size={16} className="animate-spin" />
                    {bulkDeleteProgress.current}/{bulkDeleteProgress.total}
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Alle APPROVED löschen ({templates.filter(t => t.status === 'APPROVED').length})
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Bulk Delete Progress */}
      {bulkDeleting && (
        <div className="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-red-700">
              Löschen... {bulkDeleteProgress.current} von {bulkDeleteProgress.total}
            </span>
            <span className="text-sm text-red-500">
              {Math.round((bulkDeleteProgress.current / bulkDeleteProgress.total) * 100)}%
            </span>
          </div>
          <div className="w-full bg-red-200 rounded-full h-2">
            <div 
              className="bg-red-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%` }}
            />
          </div>
          {bulkDeleteProgress.results.length > 0 && (
            <div className="mt-3 max-h-32 overflow-y-auto text-xs">
              {bulkDeleteProgress.results.slice(-5).map((r, i) => (
                <div key={i} className={`flex items-center gap-2 py-1 ${r.success ? 'text-green-600' : 'text-red-600'}`}>
                  {r.success ? '✅' : '❌'} {r.name}
                  {r.error && <span className="text-gray-500">- {r.error}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Templates Liste */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500">Noch keine Templates vorhanden</p>
            <button
              onClick={() => setShowCreateDialog(true)}
              disabled={!wabaConfigured}
              data-testid="create-first-template-btn"
              className="mt-4 text-green-600 hover:text-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Erstes Template erstellen
            </button>
          </div>
        ) : (
          templates.map((template) => (
            <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  {getStatusIcon(template.status)}
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(template.status)}`}>
                    {template.status}
                  </span>
                </div>
                <button
                  onClick={() => deleteTemplate(template.name)}
                  className="text-gray-400 hover:text-red-600"
                  title="Löschen"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
              
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                  {template.category}
                </span>
                <span className="text-xs">{template.language}</span>
              </div>

              {/* Roh-Vorschau des Body-Texts mit Platzhaltern */}
              <div className="mt-1 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-line">
                {getBodyText(template) || 'Kein Body-Text vorhanden'}
              </div>

              {template.rejected_reason && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                  <strong>Grund:</strong> {template.rejected_reason}
                </div>
              )}

              {template.quality_score && (
                <div className="mt-2 text-xs text-gray-500">
                  Qualität: {template.quality_score.score}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create Template Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Neues Template erstellen</h2>
              <p className="text-sm text-gray-600 mt-1">
                Templates müssen von Meta genehmigt werden, bevor Sie verwendet werden können
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Template Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="z.B. terminerinnerung_2024"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Nur Kleinbuchstaben, Zahlen und Unterstriche erlaubt
                </p>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kategorie *
                </label>
                <select
                  value={newTemplate.category}
                  onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="UTILITY">UTILITY - Transaktional (Termine, Rechnungen)</option>
                  <option value="MARKETING">MARKETING - Werbung und Angebote</option>
                  <option value="AUTHENTICATION">AUTHENTICATION - Verifizierungscodes</option>
                </select>
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sprache *
                </label>
                <select
                  value={newTemplate.language}
                  onChange={(e) => setNewTemplate({ ...newTemplate, language: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="en_US">English (US) - Empfohlen ✅</option>
                  <option value="de">Deutsch (⚠️ Möglicherweise gesperrt)</option>
                  <option value="de_CH">Deutsch (Schweiz)</option>
                  <option value="fr">Français</option>
                  <option value="it">Italiano</option>
                </select>
                {newTemplate.language === 'de' && (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ Hinweis: Deutsch könnte für 4 Wochen gesperrt sein, falls kürzlich ein Template gelöscht wurde
                  </p>
                )}
              </div>

              {/* Header (Optional) */}
              <div>
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Header hinzufügen (optional)</span>
                </label>
                {hasHeader && (
                  <input
                    type="text"
                    value={headerText}
                    onChange={(e) => setHeaderText(e.target.value)}
                    placeholder="z.B. Terminerinnerung für {{1}}"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                )}
              </div>

              {/* Body (Required) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nachrichtentext * (Body)
                </label>
                <textarea
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  placeholder="Hallo {{1}}, Ihr Termin ist am {{2}} um {{3}} Uhr."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Verwenden Sie {'{{1}}'}, {'{{2}}'}, etc. für Variablen. Max. 1024 Zeichen.
                </p>
              </div>

              {/* Footer (Optional) */}
              <div>
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={hasFooter}
                    onChange={(e) => setHasFooter(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Footer hinzufügen (optional)</span>
                </label>
                {hasFooter && (
                  <input
                    type="text"
                    value={footerText}
                    onChange={(e) => setFooterText(e.target.value)}
                    placeholder="z.B. StrongFit Studio - Ihr Fitness-Partner"
                    maxLength={60}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                )}
              </div>

              {/* Buttons (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Buttons (optional)
                </label>
                <div className="space-y-2 mb-2">
                  {buttons.map((button, index) => (
                    <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={button.text}
                          onChange={(e) => updateButton(index, 'text', e.target.value)}
                          placeholder="Button Text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        {button.type === 'PHONE_NUMBER' && (
                          <input
                            type="text"
                            value={button.phone_number}
                            onChange={(e) => updateButton(index, 'phone_number', e.target.value)}
                            placeholder="+41781234567"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        )}
                        {button.type === 'URL' && (
                          <input
                            type="text"
                            value={button.url}
                            onChange={(e) => updateButton(index, 'url', e.target.value)}
                            placeholder="https://example.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        )}
                      </div>
                      <button
                        onClick={() => removeButton(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                {buttons.length < 3 && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => addButton('QUICK_REPLY')}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      + Quick Reply
                    </button>
                    <button
                      onClick={() => addButton('PHONE_NUMBER')}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      + Telefon
                    </button>
                    <button
                      onClick={() => addButton('URL')}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      + URL
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              {submitSuccess ? (
                <div className="flex items-center gap-2 px-6 py-3 bg-green-100 text-green-800 rounded-lg">
                  <CheckCircle size={20} />
                  <span className="font-medium">✅ Erfolgreich eingereicht! Wird zur Genehmigung gesendet...</span>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setShowCreateDialog(false);
                      resetForm();
                    }}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={createTemplate}
                    disabled={isSubmitting}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                      isSubmitting 
                        ? 'bg-gray-400 text-white cursor-not-allowed' 
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Wird eingereicht...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Zur Genehmigung einreichen
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Template Dialog */}
      {editingTemplate && (
        <EditTemplateDialog
          template={editingTemplate}
          onSave={handleSaveTemplate}
          onClose={() => setEditingTemplate(null)}
          onUploadToMeta={handleUploadToMeta}
          onRefresh={fetchTemplates}
        />
      )}
    </div>
  );
};

export default WhatsAppTemplatesPage;
